/**
 * Fixed Route API Client
 * 
 * Client for calling UAT or Production APIs for fixed route operations:
 * - getnetworksettings: Fetch available networks/products
 * - createrouteversion: Create routes in UAT/Production
 * 
 * Handles authentication, retries, and error parsing.
 * 
 * NOTE: All requests are proxied through the backend to avoid CORS issues.
 * The UAT API only allows requests from operations-portal-uat.liftango.com.
 * Production API has similar restrictions.
 */

import { getAtlasApiBaseUrl } from './getAtlasApiBaseUrl';
import { atlasFetch } from './api/atlasBackendClient';
import { extractUATRoutes } from './fixedRouteUatImporter';
import {
  getEnvironmentApiBaseUrl,
  getEnvironmentPortalOrigin,
  type Environment,
} from './environmentConfig';

export type { Environment } from './environmentConfig';

// Backend API base URL
const BACKEND_API_BASE_URL = getAtlasApiBaseUrl();

/**
 * Get API base URL based on environment
 */
function getApiBaseUrl(environment: Environment = 'UAT'): string {
  return getEnvironmentApiBaseUrl(environment);
}

/**
 * Get backend proxy URL (same endpoint handles both UAT and Production)
 */
function getProxyUrl(): string {
  return `${BACKEND_API_BASE_URL}/api/uat/proxy`;
}

/**
 * Get API URLs for a specific environment
 */
function getApiUrls(environment: Environment = 'UAT') {
  const baseUrl = getApiBaseUrl(environment);
  return {
    baseUrl,
    opsApiUrl: `${baseUrl}/context/ops/q`,
    fixedRouteApiUrl: `${baseUrl}/context/fixed-route/c`,
    fixedRouteQueryUrl: `${baseUrl}/context/fixed-route/q`,
  };
}

export interface UATNetwork {
  productId: string;
  productName: string;
  totalNetworks: number;
  defaultNetworkId: number;
  /** Network UUID (string) required by the ODB shift-solutions API as `networkId`. Distinct from `defaultNetworkId` (numeric dbId). */
  networkUuid?: string;
  activeMaintenance: string | null;
  image?: string;
  timezone?: string; // Network timezone (e.g., "Australia/Sydney")
  networkType?: 'FR' | 'ODB' | 'Mixed'; // Network type: Fixed Route, On-Demand, or Mixed
}

export interface UATNetworksResponse {
  status: number;
  data: {
    entry: UATNetwork[];
  };
}

/**
 * Network Settings from UAT
 */
export interface UATNetworkSettings {
  id: string;
  dbId: number;
  timezone: string;
  concessions: any[];
  userManagement: string;
  dateRideRestriction: number;
  dateRideRestrictionOpsBookings?: number | null;
  dateRideRestrictionMin?: number | null;
  accessibilityRequirementsEnabled: boolean;
  mobilityRequirementsEnabled: boolean;
  address?: {
    id: string;
    dbId: number;
    label: string;
    latitude: number;
    longitude: number;
    placeId?: string;
  };
  luggageSizePerRideLimit?: number;
  luggagePerTypeLimit?: any;
  mobileCountryCode?: string;
  mobileCountryDialCode?: string;
  maxVehicleCapacities?: {
    fixedRoute?: {
      standard: number;
      wheelchair: number;
      luggage: number;
    };
    onDemand?: {
      standard: number;
      wheelchair: number;
      luggage: number;
    };
  };
  dateFormat?: string;
  timeFormat?: string;
  dateFormatLong?: string;
  timeFormatLong?: string;
  measurementUnit?: string;
  locale?: string;
  currencyCode?: string;
  payments?: {
    enabled: boolean;
    methods: any[];
  };
  driverDirectionsMapProvider?: string;
  allowedAddressTypes?: any[];
}

export interface UATNetworkSettingsResponse {
  status: number;
  data: {
    networks: Array<{
      networkSettings: UATNetworkSettings;
      luggageTypes?: any[];
      creditBundles?: any[];
      networkMembershipSettings?: any;
      features?: Array<{
        enabled: boolean;
        name: string;
      }>;
      languages?: any[];
      networkCustomerEntities?: any[];
      networkCustomisations?: Array<{
        key: string;
        value: string | null;
        enabled: boolean;
      }>;
    }>;
  };
}

export interface UATStopListItem {
  id: string;
  name?: string;
  label?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  latitude?: number;
  longitude?: number;
  address?: string;
  description?: string;
  placeId?: string;
}

export interface UATStopsListResponse {
  status: number;
  data: {
    entry?: UATStopListItem[];
    entries?: UATStopListItem[];
    stops?: UATStopListItem[];
  };
  total?: number;
  offset?: number;
  limit?: number;
}

export interface UATRouteVersionResponse {
  status: number;
  data: {
    status?: number;
    success?: boolean;
    routeId?: string | null;
    createdRouteVersionId?: string | null;
    error?: string;
    message?: string;
    data?: {
      success?: boolean;
      routeId?: string | null;
      createdRouteVersionId?: string | null;
      error?: string;
    };
  };
  message?: string;
}

export interface UATErrorResponse {
  status: number;
  message: string;
  errors?: string[];
}

/**
 * UAT Stop data from route stops
 */
export interface UATStop {
  id: string;
  name?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  order?: number;
  timeToNextStop?: number;
  timeSpentAtStop?: number;
  address?: string;
  description?: string;
}

/**
 * UAT Route data from operations solutions
 */
export interface UATRoute {
  id: string;
  label: string;
  version?: string;
  colour?: string;
  routeShape?: {
    orderedCoordinates?: [number, number][];
  };
  stops?: UATStop[];
  routeStops?: {
    history?: any[];
    next?: Array<{
      id: string;
      name?: string;
      coordinates?: {
        lat: number;
        lng: number;
      };
      expectedArrivalTime?: string;
      order?: number;
    }>;
  };
}

/**
 * UAT Shift Solution data
 */
export interface UATShiftSolution {
  id: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  shiftType?: string;
  direction?: string;
  startStopIndex?: number;
  numberOfRepeats?: number;
  vehicle?: {
    id: string;
    name?: string;
    picture?: string;
    capacities?: any[];
  };
  driver?: {
    id: string;
    displayName?: string;
    picture?: string;
  };
  route?: UATRoute;
}

/**
 * UAT Operations Solution entry
 */
export interface UATOperationsSolution {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  shiftSolutions?: UATShiftSolution[];
}

/**
 * Response from getoperationssolutionsall endpoint
 */
export interface UATOperationsSolutionsResponse {
  status: number;
  data: {
    entries: UATOperationsSolution[];
  };
}

/**
 * UAT Shift Solution Schedule (for on-demand fleets)
 */
export interface UATShiftSchedule {
  scheduleId: string;
  startTime: string;
  endTime: string;
  driver: {
    id: string;
    displayName?: string;
  } | null;
  vehicle: {
    id: string;
    label: string;
    seatingCapacity: Array<{
      amount: number;
      capacityName: string;
      displayOrder: number;
    }>;
  };
  activities: any[];
  stopActivities: any;
  breaks: any[];
  events: any;
  paths: any;
  stops: any[];
}

/**
 * UAT Shift Solution (for on-demand fleets)
 */
export interface UATShiftSolution {
  solutionId: string;
  name: string;
  startTime: string;
  endTime: string;
  schedules: UATShiftSchedule[];
  service: {
    breaksEnabled: boolean;
    rideLockTimeOffset: number;
  };
}

/**
 * Response from getshiftsolutions endpoint (on-demand fleets)
 */
export interface UATShiftSolutionsResponse {
  status: number;
  data: {
    solutions: UATShiftSolution[];
  };
}

/**
 * Generate session and trace IDs for UAT API calls
 */
function generateSessionId(): string {
  return `ops-${crypto.randomUUID()}`;
}

function generateTraceId(): string {
  return `ops-${crypto.randomUUID()}`;
}

/**
 * Normalize JWT token - remove "JWT " or "Bearer " prefix if present, and trim whitespace
 */
function normalizeJwtToken(token: string): string {
  let normalized = token.trim();
  // Remove "JWT " prefix if present
  if (normalized.startsWith('JWT ')) {
    normalized = normalized.substring(4);
  }
  // Remove "Bearer " prefix if present
  if (normalized.startsWith('Bearer ')) {
    normalized = normalized.substring(7);
  }
  return normalized.trim();
}

/**
 * Create headers for UAT/Production API requests
 */
export function createHeaders(jwtToken: string, productId?: string, environment: Environment = 'UAT'): HeadersInit {
  // Normalize the token to handle cases where user pastes "JWT token" or "Bearer token"
  const normalizedToken = normalizeJwtToken(jwtToken);

  // EUWE2 enforces strict Origin/Referer checks and returns an opaque 500 on
  // mismatch (before reaching auth), so this must match the region's portal.
  const origin = getEnvironmentPortalOrigin(environment);
  const referer = `${origin}/`;
  
  const headers: HeadersInit = {
    'accept': 'application/json, text/plain, */*',
    'accept-language': 'en-US,en;q=0.9',
    'authorization': `JWT ${normalizedToken}`,
    'content-type': 'application/json',
    'origin': origin,
    'referer': referer,
    'sec-ch-ua': '"Not)A;Brand";v="8", "Chromium";v="138", "Google Chrome";v="138"',
    'sec-ch-ua-mobile': '?0',
    'sec-ch-ua-platform': '"Windows"',
    'sec-fetch-dest': 'empty',
    'sec-fetch-mode': 'cors',
    'sec-fetch-site': 'same-site',
    'user-agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/138.0.0.0 Safari/537.36',
    'x-lifty-session-id': generateSessionId(),
    'x-lifty-trace-id': generateTraceId(),
  };

  if (productId) {
    headers['x-lifty-product-id'] = productId;
  }

  return headers;
}

/**
 * Make API request through backend proxy with exponential backoff retry
 */
export async function makeRequestWithRetry(
  uatUrl: string,
  method: string,
  headers: HeadersInit,
  params?: Record<string, string>,
  body?: any,
  maxRetries: number = 5,
  initialDelay: number = 1000,
  environment: Environment = 'UAT',
  // Whether to auto-retry on 5xx (500/502/503/504). Safe for reads, but NOT for
  // non-idempotent writes: a 5xx after a slow backend may mean the write already
  // committed, so retrying can create duplicates. Callers that POST a creation
  // should pass false. 429 (rate limit) is always retried — no work was done.
  retryServerErrors: boolean = true,
): Promise<{ status: number; data: any }> {
  let lastError: Error | null = null;

  // Convert HeadersInit to plain object
  const headersObj: Record<string, string> = {};
  if (headers instanceof Headers) {
    headers.forEach((value, key) => {
      headersObj[key] = value;
    });
  } else if (Array.isArray(headers)) {
    headers.forEach(([key, value]) => {
      headersObj[key] = value;
    });
  } else {
    Object.assign(headersObj, headers);
  }

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const proxyRequest = {
        url: uatUrl,
        method,
        headers: headersObj,
        params: params || {},
        body: body || null,
      };

      const response = await atlasFetch(getProxyUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(proxyRequest),
      });

      if (!response.ok) {
        // If proxy itself fails, throw
        throw new Error(`Proxy request failed: ${response.status} ${response.statusText}`);
      }

      const proxyResponse = await response.json();

      // Retry on 429 (rate limit — always safe) and, when enabled, 5xx from the
      // UAT API. 5xx retries are gated by `retryServerErrors` because retrying a
      // non-idempotent write whose 5xx masked a committed change would duplicate it.
      const isRateLimited = proxyResponse.status === 429;
      const isServerError =
        proxyResponse.status === 500 ||
        proxyResponse.status === 502 ||
        proxyResponse.status === 503 ||
        proxyResponse.status === 504;
      if ((isRateLimited || (isServerError && retryServerErrors)) && attempt < maxRetries - 1) {
        const delay = initialDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      return proxyResponse;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      
      if (attempt < maxRetries - 1) {
        const delay = initialDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError || new Error('Request failed after retries');
}

/**
 * Fetch network settings for a specific network ID (internal helper)
 * 
 * @param jwtToken - JWT authentication token
 * @param networkId - Network database ID
 * @param environment - Environment to fetch from ('UAT' or 'Production'), defaults to 'UAT'
 * @returns Promise resolving to network settings with timezone
 */
export async function fetchNetworkSettingsByNetworkId(
  jwtToken: string,
  networkId: number,
  environment: Environment = 'UAT'
): Promise<{ timezone?: string; maxVehicleCapacities?: any; fullSettings?: any; networkUuid?: string } | null> {
  // Try both fixed_route and operations contexts to get complete network settings
  const contexts = ['fixed_route', 'operations'] as const;
  const { fixedRouteQueryUrl, opsApiUrl } = getApiUrls(environment);
  
  for (const aclContext of contexts) {
    try {
      const params: Record<string, string> = {
        type: 'getnetworksettings',
        version: '1',
        disableAccessPermissionChecks: 'true',
        aclContext,
      };

      const url = aclContext === 'fixed_route' ? fixedRouteQueryUrl : opsApiUrl;
      const response = await makeRequestWithRetry(
        url,
        'GET',
        createHeaders(jwtToken, undefined, environment),
        params,
        undefined,
        5,
        1000,
        environment
      );

      if (response.status === 200 && response.data) {
        const uatResponse = response.data;
        // Check various response structures
        const networks = uatResponse.data?.networks || 
                        uatResponse.data?.data?.networks || 
                        uatResponse.networks || 
                        (Array.isArray(uatResponse.data?.entry) ? uatResponse.data.entry : []);
        
        if (Array.isArray(networks)) {
          const network = networks.find((n: any) => {
            const nId = n.networkSettings?.dbId || n.dbId || n.id;
            return nId === networkId || nId === networkId?.toString();
          });

          if (network) {
            // Some responses nest details under `networkSettings`, others return the
            // settings object directly. Normalize both shapes.
            const settings = network.networkSettings ?? network;
            console.log(`[fetchNetworkSettingsByNetworkId] Network ${networkId} with context ${aclContext} - normalized settings:`, {
              dbId: settings?.dbId,
              id: settings?.id,
              hasMaxVehicleCapacities: !!settings?.maxVehicleCapacities,
              allKeys: settings ? Object.keys(settings) : [],
            });

            const result = {
              timezone: settings?.timezone,
              maxVehicleCapacities: settings?.maxVehicleCapacities,
              networkUuid: settings?.id || network?.id,
              fullSettings: settings,
            };
            return result;
          }
        }
      }
    } catch (error) {
      console.warn(`[fetchNetworkSettingsByNetworkId] Failed to fetch network settings for network ${networkId} with context ${aclContext}:`, error);
    }
  }
  
  return null;
}

/**
 * Fetch available networks from UAT or Production
 * 
 * @param jwtToken - JWT authentication token
 * @param environment - Environment to fetch from ('UAT' or 'Production'), defaults to 'UAT'
 * @returns Promise resolving to networks response
 */
export async function fetchNetworks(jwtToken: string, environment: Environment = 'UAT'): Promise<UATNetworksResponse> {
  // Use getnetworkallproducts endpoint which returns all products
  // This is more comprehensive than getnetworksettings which may be filtered by permissions
  const params: Record<string, string> = {
    type: 'getnetworkallproducts',
    version: '1',
    resource: 'products',
    operation: 'list',
    aclContext: 'fixed_route',
  };

  const { fixedRouteQueryUrl } = getApiUrls(environment);

  const response = await makeRequestWithRetry(
    fixedRouteQueryUrl,
    'GET',
    createHeaders(jwtToken, undefined, environment),
    params,
    undefined,
    5,
    1000,
    environment
  );
  
  // Also fetch network settings to get timezone information
  // This endpoint returns full network data including networkSettings.timezone
  let networkSettingsData: any = null;
  try {
    const networkSettingsParams: Record<string, string> = {
      type: 'getnetworksettings',
      version: '1',
      disableAccessPermissionChecks: 'true',
      aclContext: 'fixed_route',
    };
    
    const { fixedRouteQueryUrl } = getApiUrls(environment);
    const networkSettingsResponse = await makeRequestWithRetry(
      fixedRouteQueryUrl,
      'GET',
      createHeaders(jwtToken, undefined, environment),
      networkSettingsParams,
      undefined,
      5,
      1000,
      environment
    );
    
    if (networkSettingsResponse.status === 200 && networkSettingsResponse.data) {
      networkSettingsData = networkSettingsResponse.data;
      // Log the structure to help debug
      const networksInSettings = networkSettingsData?.data?.networks || 
                                  networkSettingsData?.data?.data?.networks || 
                                  networkSettingsData?.networks || [];
      console.log('[fetchUATNetworks] Successfully fetched network settings for timezone lookup', {
        hasData: !!networkSettingsData.data,
        hasDataNetworks: !!networkSettingsData.data?.networks,
        networksCount: Array.isArray(networksInSettings) ? networksInSettings.length : 0,
        sampleNetwork: Array.isArray(networksInSettings) && networksInSettings.length > 0 ? {
          hasNetworkSettings: !!networksInSettings[0].networkSettings,
          hasTimezone: !!networksInSettings[0].networkSettings?.timezone,
          timezone: networksInSettings[0].networkSettings?.timezone,
          dbId: networksInSettings[0].networkSettings?.dbId,
        } : null,
      });
    }
  } catch (error) {
    console.warn('[fetchUATNetworks] Failed to fetch network settings (timezone may be missing):', error);
  }

  if (response.status !== 200) {
    const errorData: UATErrorResponse = typeof response.data === 'object' && response.data
      ? response.data
      : {
          status: response.status,
          message: `HTTP ${response.status}`,
        };

    // Provide more helpful error messages
    if (response.status === 401) {
      throw new Error(`Authentication failed: ${errorData.message || 'Invalid or expired JWT token. Please check your token and try again.'}`);
    }

    throw new Error(`Failed to fetch networks: ${errorData.message || `HTTP ${response.status}`}`);
  }

  // The proxy returns { status, headers, data } where data is the UAT API response
  // UAT API response is { status, data: { entry: [...] } }
  // So response.data is the UAT API response object
  const uatResponse = response.data;
  
  // Validate response structure
  if (!uatResponse || typeof uatResponse !== 'object') {
    console.error('[fetchUATNetworks] Invalid response structure:', response);
    throw new Error('Invalid response structure from UAT API');
  }
  
  // If standard product-entry shape is present, keep processing through the
  // enrichment pipeline below so each product gets networkUuid/timezone/type.
  if (Array.isArray(uatResponse.data?.entry)) {
    console.log(`[fetchUATNetworks] Loaded ${uatResponse.data.entry.length} product entries; enriching with network UUID/timezone/type`);
    if (uatResponse.data.entry.length === 0) {
      console.warn('[fetchUATNetworks] API returned 0 networks. This may indicate:');
      console.warn('  - The JWT token does not have access to any networks');
      console.warn('  - The user account needs additional permissions');
      console.warn('  - The API endpoint or parameters may be incorrect');
    }
  }
  
  // Log the actual structure for debugging
  console.warn('[fetchUATNetworks] Unexpected response structure:', {
    hasStatus: !!uatResponse.status,
    hasData: !!uatResponse.data,
    dataType: typeof uatResponse.data,
    hasEntry: !!(uatResponse.data && uatResponse.data.entry),
    entryIsArray: !!(uatResponse.data && Array.isArray(uatResponse.data.entry)),
    hasNestedData: !!(uatResponse.data && uatResponse.data.data),
    hasNestedEntry: !!(uatResponse.data && uatResponse.data.data && uatResponse.data.data.entry),
    hasNestedNetworks: !!(uatResponse.data && uatResponse.data.data && uatResponse.data.data.networks),
    topLevelKeys: Object.keys(uatResponse),
    dataKeys: uatResponse.data ? Object.keys(uatResponse.data) : [],
    nestedDataKeys: uatResponse.data?.data ? Object.keys(uatResponse.data.data) : [],
    fullResponse: uatResponse
  });
  
  // Try to extract networks from various possible structures
  let networks: any[] = [];
  
  // Option 1: Standard structure { status, data: { entry: [...] } }
  if (Array.isArray(uatResponse.data?.entry)) {
    networks = uatResponse.data.entry;
    console.log(`[fetchUATNetworks] Found products in data.entry: ${networks.length} products`);
  }
  // Option 1b: Nested entry structure { status, data: { status, data: { entry: [...] } } }
  else if (uatResponse.data?.data && Array.isArray(uatResponse.data.data.entry)) {
    networks = uatResponse.data.data.entry;
    console.log(`[fetchUATNetworks] Found products in data.data.entry: ${networks.length} products`);
  }
  // Option 2: Nested structure { status, data: { status, data: { networks: [...] } } }
  else if (uatResponse.data?.data && Array.isArray(uatResponse.data.data.networks)) {
    networks = uatResponse.data.data.networks;
    console.log(`[fetchUATNetworks] Found networks in data.data.networks: ${networks.length} networks`);
  }
  // Option 3: Direct networks array { status, data: { networks: [...] } }
  else if (Array.isArray(uatResponse.data?.networks)) {
    networks = uatResponse.data.networks;
    console.log(`[fetchUATNetworks] Found networks in data.networks: ${networks.length} networks`);
  }
  // Option 4: Direct entry array { status, data: { entry: [...] } }
  else if (Array.isArray(uatResponse.entry)) {
    networks = uatResponse.entry;
    console.log(`[fetchUATNetworks] Found networks in entry: ${networks.length} networks`);
  }
  // Option 5: Direct networks array { status, networks: [...] }
  else if (Array.isArray(uatResponse.networks)) {
    networks = uatResponse.networks;
    console.log(`[fetchUATNetworks] Found networks in networks: ${networks.length} networks`);
  }
  else {
    console.warn('[fetchUATNetworks] Could not find networks array in response:', {
      hasData: !!uatResponse.data,
      hasNestedData: !!(uatResponse.data && uatResponse.data.data),
      hasNetworks: !!(uatResponse.data && uatResponse.data.networks),
      hasNestedNetworks: !!(uatResponse.data && uatResponse.data.data && uatResponse.data.data.networks),
      hasEntry: !!(uatResponse.data && uatResponse.data.entry),
      responseKeys: Object.keys(uatResponse),
      dataKeys: uatResponse.data ? Object.keys(uatResponse.data) : []
    });
  }
  
  if (networks.length === 0) {
    console.warn('[fetchUATNetworks] API returned 0 networks. This may indicate:');
    console.warn('  - The JWT token does not have access to any networks');
    console.warn('  - The user account needs additional permissions');
    console.warn('  - The API endpoint or parameters may be incorrect');
  } else {
    console.log(`[fetchUATNetworks] Successfully extracted ${networks.length} networks`);
  }
  
  // The networks array contains full network objects, but we need product-level entries
  // The getnetworksettings API should return product-level entries, not individual networks
  // If we're getting networks instead of products, we need to check if there's a different endpoint
  // or if the response structure is different than expected
  
  // First, check if the networks array already contains product-level entries (like in UATNetworks file)
  // Product entries have: productId, productName, totalNetworks, defaultNetworkId
  // Network objects have: networkSettings with dbId, and other network-specific fields
  
  const firstItem = networks[0];
  const hasProductStructure = firstItem && (
    firstItem.productId !== undefined ||
    (firstItem.productName !== undefined && firstItem.totalNetworks !== undefined)
  );
  
  if (hasProductStructure) {
    // Already product-level entries - keep this load lightweight and avoid
    // N additional network settings requests on initial list load.
    console.log(`[fetchUATNetworks] Networks array contains product-level entries, using lightweight enrichment`);

    const possibleNetworkArrays = [
      networkSettingsData?.data?.networks,
      networkSettingsData?.data?.data?.networks,
      networkSettingsData?.networks,
      uatResponse.data?.data?.networks,
      uatResponse.data?.data?.data?.networks,
      uatResponse.data?.networks,
      uatResponse.networks,
    ].filter((arr) => Array.isArray(arr));

    const enrichedNetworks = networks.map((product: any) => {
      let rawNetworkData: any = null;
      for (const networkArray of possibleNetworkArrays) {
        rawNetworkData = (networkArray as any[]).find((n: any) => {
          const networkId = n.networkSettings?.dbId || n.dbId || n.id;
          return networkId === product.defaultNetworkId || networkId === product.defaultNetworkId?.toString();
        });
        if (rawNetworkData) break;
      }

      const capacities = rawNetworkData?.networkSettings?.maxVehicleCapacities;
      let networkType: 'FR' | 'ODB' | 'Mixed' | undefined = product.networkType;
      if (capacities) {
        const hasFixedRoute = !!capacities.fixedRoute;
        const hasOnDemand = !!capacities.onDemand;
        if (hasFixedRoute && hasOnDemand) networkType = 'Mixed';
        else if (hasFixedRoute) networkType = 'FR';
        else if (hasOnDemand) networkType = 'ODB';
      }

      return {
        ...product,
        networkUuid: rawNetworkData?.networkSettings?.id as string | undefined,
        timezone: product.timezone || rawNetworkData?.networkSettings?.timezone,
        networkType,
      };
    });
    
    // Log summary of network types detected
    const typeCounts = enrichedNetworks.reduce((acc, n) => {
      const type = n.networkType || 'FR';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    console.log(`[fetchUATNetworks] Network type summary:`, typeCounts);
    console.log(`[fetchUATNetworks] Total networks: ${enrichedNetworks.length}, with types: ${Object.keys(typeCounts).length} unique types`);
    
    return {
      status: uatResponse.status || response.status,
      data: {
        entry: enrichedNetworks
      }
    } as UATNetworksResponse;
  }
  
  // Otherwise, we have individual network objects - need to group by product
  // Check if networks have productId field (may be nested in networkSettings or at root)
  const productMap = new Map<string, any>();
  
  networks.forEach((network: any) => {
    // Try to extract product info - check multiple possible locations
    const networkId = network.networkSettings?.dbId || network.dbId || network.id;
    
    // Product ID might be at root, in networkSettings, or we need to infer it
    let productId = network.productId;
    if (!productId && network.networkSettings) {
      productId = network.networkSettings.productId;
    }
    // If still no productId, check if there's a product reference
    if (!productId && network.product) {
      productId = network.product.id || network.product.productId;
    }
    
    // Product name
    let productName = network.productName;
    if (!productName && network.networkSettings) {
      productName = network.networkSettings.name || network.networkSettings.productName;
    }
    if (!productName && network.product) {
      productName = network.product.name || network.product.productName;
    }
    
    // If we can't find product info, treat each network as its own product (fallback)
    if (!productId) {
      productId = `network-${networkId}`;
      productName = productName || `Network ${networkId}`;
    }
    
    // Extract timezone and network type from networkSettings if available
    const networkTimezone = network.networkSettings?.timezone;
    const capacities = network.networkSettings?.maxVehicleCapacities;
    let networkType: 'FR' | 'ODB' | 'Mixed' | undefined = undefined;
    if (capacities) {
      const hasFixedRoute = !!capacities.fixedRoute;
      const hasOnDemand = !!capacities.onDemand;
      if (hasFixedRoute && hasOnDemand) {
        networkType = 'Mixed';
      } else if (hasFixedRoute) {
        networkType = 'FR';
      } else if (hasOnDemand) {
        networkType = 'ODB';
      }
    }
    
    if (!productMap.has(productId)) {
      productMap.set(productId, {
        productId,
        productName: productName || `Product ${productId}`,
        totalNetworks: 0,
        defaultNetworkId: networkId,
        networkUuid: network.networkSettings?.id || network.id,
        activeMaintenance: network.activeMaintenance || null,
        image: network.image || '',
        timezone: networkTimezone,
        networkType,
      });
    }
    const product = productMap.get(productId)!;
    product.totalNetworks++;
    // Use the first network as default if not set
    if (!product.defaultNetworkId) {
      product.defaultNetworkId = networkId;
    }
    // Update timezone if not set and this network has one
    if (!product.timezone && networkTimezone) {
      product.timezone = networkTimezone;
    }
    // Update network type if not set and this network has one
    // If we find a Mixed type, prioritize it; otherwise use the first type found
    if (!product.networkType && networkType) {
      product.networkType = networkType;
    } else if (networkType === 'Mixed') {
      product.networkType = 'Mixed';
    }
  });
  
  const entry = Array.from(productMap.values());
  console.log(`[fetchUATNetworks] Grouped ${networks.length} networks into ${entry.length} products`);
  console.log(`[fetchUATNetworks] Sample network structure:`, firstItem ? {
    keys: Object.keys(firstItem),
    hasNetworkSettings: !!firstItem.networkSettings,
    hasProductId: !!firstItem.productId,
    networkSettingsKeys: firstItem.networkSettings ? Object.keys(firstItem.networkSettings) : []
  } : 'No networks');
  
  return {
    status: uatResponse.status || response.status,
    data: {
      entry
    }
  } as UATNetworksResponse;
}

/**
 * Create a route version in UAT or Production
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID (from network selection)
 * @param payload - Route creation payload
 * @param environment - Environment to create route in ('UAT' or 'Production'), defaults to 'UAT'
 * @returns Promise resolving to route creation response
 */
export interface UATDeleteRouteResponse {
  status: number;
  success: boolean;
  message?: string;
  error?: string;
  data?: {
    success: boolean;
    message?: string;
    error?: string;
  };
}

/**
 * Delete a route from UAT/Production
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID (x-lifty-product-id header)
 * @param routeId - Route ID (UUID string) to delete
 * @param deleteUnconnectedStops - Whether to also delete stops not used by other routes (default: false)
 * @param environment - Target environment ('UAT' or 'Production')
 * @returns Promise with deletion response
 */
export async function deleteRoute(
  jwtToken: string,
  productId: string,
  routeId: string,
  deleteUnconnectedStops: boolean = false,
  environment: Environment = 'UAT'
): Promise<UATDeleteRouteResponse> {
  // Validate JWT token
  if (!jwtToken || !jwtToken.trim()) {
    throw new Error('JWT token is required');
  }

  // Validate routeId
  if (!routeId || !routeId.trim()) {
    throw new Error('Route ID is required');
  }

  const { fixedRouteApiUrl } = getApiUrls(environment);
  const headers = createHeaders(jwtToken, productId, environment);

  // Build deletion payload
  const payload = {
    type: 'deleteroute',
    version: '1',
    aclConfig: {
      resource: 'routes',
      operation: 'delete',
    },
    aclContext: 'fixed_route',
    routeId: routeId.trim(),
    deleteUnconnectedStops: deleteUnconnectedStops,
  };

  const response = await makeRequestWithRetry(
    fixedRouteApiUrl,
    'POST',
    headers,
    undefined,
    payload,
    5,
    1000,
    environment,
    // Don't auto-retry the delete write on 5xx: a 5xx can mask a delete that
    // committed, and retrying would then 404 (already gone) and look like a
    // failure. Deleting is idempotent, so the user can safely re-run. 429s are
    // still retried inside the helper.
    false,
  );

  // The API returns HTTP 200 even when the operation fails, so we also check the
  // nested success field. But a successful delete often comes back as 204 No
  // Content (or 200/202 with an empty body and no explicit `success` flag) —
  // so accept any 2xx and treat the absence of an explicit `success: false`
  // as success, mirroring the route-stop command parser.
  const responseData = response.data;
  const nestedData = responseData?.data || responseData;
  const isSuccessStatus = response.status >= 200 && response.status < 300;
  const notExplicitlyFailed = nestedData?.success !== false;

  if (isSuccessStatus && notExplicitlyFailed) {
    return {
      status: response.status,
      success: true,
      message: nestedData?.message || 'Route deleted successfully',
      data: nestedData,
    };
  } else {
    // Extract error message
    const errorMessage = nestedData?.error || nestedData?.message || responseData?.error || responseData?.message || 'Failed to delete route';
    
    return {
      status: response.status,
      success: false,
      error: errorMessage,
      data: nestedData,
    };
  }
}

export async function createRouteVersion(
  jwtToken: string,
  productId: string,
  payload: any,
  environment: Environment = 'UAT',
  idempotencyKey?: string,
): Promise<UATRouteVersionResponse> {
  // Validate JWT token
  if (!jwtToken || !jwtToken.trim()) {
    throw new Error('JWT token is required');
  }

  const { fixedRouteApiUrl } = getApiUrls(environment);
  const headers = createHeaders(jwtToken, productId, environment) as Record<string, string>;
  // /api/uat/proxy forwards only allowlisted headers; Idempotency-Key is on
  // that list, so a retried upload does not create a duplicate route version.
  if (idempotencyKey) {
    headers['Idempotency-Key'] = idempotencyKey;
  }
  
  // Extract authorization header for logging (handle different HeadersInit types)
  let authHeader = 'MISSING';
  if (headers instanceof Headers) {
    authHeader = headers.get('authorization') || 'MISSING';
  } else if (Array.isArray(headers)) {
    const authEntry = headers.find(([key]) => key.toLowerCase() === 'authorization');
    authHeader = authEntry ? authEntry[1] : 'MISSING';
  } else if (typeof headers === 'object' && headers !== null) {
    authHeader = (headers as Record<string, string>).authorization || 'MISSING';
  }
  
  // Log token usage (without exposing the full token)
  console.log('[createRouteVersion] Using JWT token:', {
    hasToken: !!jwtToken,
    tokenLength: jwtToken.length,
    tokenPrefix: jwtToken.substring(0, 20) + '...',
    authorizationHeader: authHeader ? `${authHeader.substring(0, 30)}...` : 'MISSING',
    productId,
    environment,
  });
  
  const response = await makeRequestWithRetry(
    fixedRouteApiUrl,
    'POST',
    headers,
    undefined,
    payload,
    5,
    1000,
    environment,
    // createrouteversion is a non-idempotent write. Don't auto-retry it on 5xx:
    // a slow EUWE2 backend can return 502/504 *after* the version committed, so
    // retrying would create duplicates. We fail fast instead and tell the user
    // to check Ops. (429 rate-limits are still retried inside the helper.)
    false,
  );

  // The API returns HTTP 200 even when the operation fails
  // We need to check the nested success field: response.data.data.success
  const responseData = response.data;
  const nestedData = responseData?.data || responseData;

  // Accept any 2xx — some gateways/regions (e.g. EUWE2) return 201 Created for a
  // successful createrouteversion, which previously threw despite success.
  const isSuccessStatus = response.status >= 200 && response.status < 300;
  if (!isSuccessStatus) {
    // Log the full response for debugging
    console.error('[createRouteVersion] API error response:', {
      status: response.status,
      data: response.data,
      dataType: typeof response.data,
      dataKeys: response.data && typeof response.data === 'object' ? Object.keys(response.data) : [],
    });

    const errorData: UATErrorResponse = typeof response.data === 'object' && response.data
      ? response.data
      : {
          status: response.status,
          message: `HTTP ${response.status}`,
        };

    // Extract detailed error message
    let errorMessage = errorData.message || `HTTP ${response.status}`;
    
    // Check for additional error details in the response
    if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
      errorMessage = errorData.errors.join('; ');
    } else if (typeof response.data === 'object' && response.data) {
      // Check for common error fields
      const data = response.data as any;
      if (data.error) {
        errorMessage = typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
      } else if (data.message) {
        errorMessage = typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
      } else if (data.errors && Array.isArray(data.errors)) {
        errorMessage = data.errors.join('; ');
      } else if (data.data?.error) {
        // Some APIs nest errors in data.error
        errorMessage = typeof data.data.error === 'string' ? data.data.error : JSON.stringify(data.data.error);
      } else if (data.data?.message) {
        errorMessage = typeof data.data.message === 'string' ? data.data.message : JSON.stringify(data.data.message);
      }
    }

    // A 5xx may mask a write that actually committed on a slow backend. Warn so
    // the user verifies in Ops rather than blindly re-uploading (which could
    // create a duplicate version).
    const maybeCommitted =
      response.status >= 500
        ? ' The route version may have been created despite this error — check Ops before re-uploading to avoid a duplicate.'
        : '';
    throw new Error(`Failed to create route: ${errorMessage}.${maybeCommitted}`);
  }

  // Check if the operation itself failed (even with HTTP 200)
  if (nestedData?.success === false) {
    const errorMessage = nestedData.error || nestedData.message || 'Upload failed';
    console.error('[createRouteVersion] Operation failed despite HTTP 200:', {
      status: response.status,
      success: nestedData.success,
      error: nestedData.error,
      message: nestedData.message,
      productId,
      environment,
      payloadNetworkId: (payload as any)?.newRouteVersionData?.networkId,
      fullResponse: response.data,
    });
    
    // Provide more helpful error message for permission denied
    if (errorMessage.toLowerCase().includes('permission denied') || errorMessage.toLowerCase().includes('permission')) {
      throw new Error(
        `Permission denied: Your JWT token does not have permission to create routes. ` +
        `Please verify:\n` +
        `1. The token has "create" permission for "routes" resource in "fixed_route" context\n` +
        `2. The product ID (${productId}) matches the token's access\n` +
        `3. The network ID in the payload matches a network you have access to\n` +
        `4. You're using the correct environment (${environment})`
      );
    }
    
    throw new Error(`Failed to create route: ${errorMessage}`);
  }

  return response.data as UATRouteVersionResponse;
}

// ---------------------------------------------------------------------------
// Route-scoped stop commands (createroutestop / updateroutestop /
// deleteroutestop / orderroutestops) against /context/fixed-route/c.
//
// Verb names and CreateRouteStopRequest payload shape are confirmed via the
// hailer repo at cypress/support/commands/on-demand/routes.ts and
// src/context/operations/application/command-factory.ts. Stops in ODB always
// belong to a specific (routeId, versionId) pair — they're not standalone.
// New stops carry inline coordinates; ODB's handler is expected to create an
// Address row internally if `addressId` is omitted (verify on first
// integration test — if rejected, we'll need a separate createaddress verb).
// ---------------------------------------------------------------------------

export interface UATRouteStopCommandResponse {
  status: number;
  success: boolean;
  message?: string;
  error?: string;
  routeStopId?: string;
  data?: {
    success?: boolean;
    routeStopId?: string | null;
    stopId?: string | null;
    message?: string;
    error?: string;
  };
}

export interface CreateRouteStopPayload {
  routeId: string;
  versionId: string;
  name: string;
  description?: string;
  order: number;
  dwellTimeAtStopMins: number;
  timeToNextStopMins: number;
  /**
   * Existing Address id to attach to the stop. If omitted, `coordinates` and
   * `label` must be provided so ODB can mint a fresh Address server-side.
   */
  addressId?: string;
  coordinates?: { latitude: number; longitude: number };
  label?: string;
  placeId?: string;
}

export interface UpdateRouteStopPayload {
  routeId: string;
  versionId: string;
  routeStopId: string;
  name?: string;
  description?: string;
  order?: number;
  dwellTimeAtStopMins?: number;
  timeToNextStopMins?: number;
  addressId?: string;
  coordinates?: { latitude: number; longitude: number };
  label?: string;
}

function parseRouteStopCommandResponse(
  response: { status: number; data: any },
  action: 'create' | 'update' | 'delete' | 'reorder',
): UATRouteStopCommandResponse {
  const responseData = response.data;
  const nestedData = responseData?.data || responseData;
  const isSuccess = response.status >= 200 && response.status < 300 && nestedData?.success !== false;
  const routeStopId =
    nestedData?.routeStopId ??
    nestedData?.data?.routeStopId ??
    nestedData?.stopId ??
    nestedData?.data?.stopId ??
    undefined;

  if (isSuccess) {
    return {
      status: response.status,
      success: true,
      message: nestedData?.message || `Route stop ${action} succeeded`,
      routeStopId: routeStopId ?? undefined,
      data: nestedData,
    };
  }

  const errorMessage =
    nestedData?.error ||
    nestedData?.message ||
    responseData?.error ||
    responseData?.message ||
    `Failed to ${action} route stop`;

  return {
    status: response.status,
    success: false,
    error: errorMessage,
    data: nestedData,
  };
}

/**
 * Add a stop to an existing route version.
 */
export async function createRouteStop(
  jwtToken: string,
  productId: string,
  payload: CreateRouteStopPayload,
  environment: Environment = 'UAT',
): Promise<UATRouteStopCommandResponse> {
  if (!jwtToken || !jwtToken.trim()) throw new Error('JWT token is required');
  if (!payload.routeId) throw new Error('routeId is required');
  if (!payload.versionId) throw new Error('versionId is required');
  if (!payload.name || !payload.name.trim()) throw new Error('Stop name is required');
  if (!payload.addressId && !payload.coordinates) {
    throw new Error('Either addressId or coordinates must be provided');
  }

  const { fixedRouteApiUrl } = getApiUrls(environment);
  const headers = createHeaders(jwtToken, productId, environment);

  const body = {
    type: 'createroutestop',
    version: '1',
    aclConfig: { resource: 'route-stops', operation: 'create' },
    aclContext: 'fixed_route',
    data: {
      routeId: payload.routeId,
      versionId: payload.versionId,
      name: payload.name,
      description: payload.description ?? '',
      order: payload.order,
      dwellTimeAtStopMins: payload.dwellTimeAtStopMins,
      timeToNextStopMins: payload.timeToNextStopMins,
      ...(payload.addressId
        ? { addressId: payload.addressId }
        : {
            address: {
              coordinates: payload.coordinates!,
              label: payload.label || payload.name,
              ...(payload.placeId && { placeId: payload.placeId }),
            },
          }),
    },
  };

  const response = await makeRequestWithRetry(
    fixedRouteApiUrl,
    'POST',
    headers,
    undefined,
    body,
    5,
    1000,
    environment,
  );
  return parseRouteStopCommandResponse(response, 'create');
}

/**
 * Update an existing stop on a route version.
 */
export async function updateRouteStop(
  jwtToken: string,
  productId: string,
  payload: UpdateRouteStopPayload,
  environment: Environment = 'UAT',
): Promise<UATRouteStopCommandResponse> {
  if (!jwtToken || !jwtToken.trim()) throw new Error('JWT token is required');
  if (!payload.routeId) throw new Error('routeId is required');
  if (!payload.versionId) throw new Error('versionId is required');
  if (!payload.routeStopId) throw new Error('routeStopId is required');

  const { fixedRouteApiUrl } = getApiUrls(environment);
  const headers = createHeaders(jwtToken, productId, environment);

  const data: Record<string, unknown> = {
    routeId: payload.routeId,
    versionId: payload.versionId,
    routeStopId: payload.routeStopId,
  };
  if (payload.name !== undefined) data.name = payload.name;
  if (payload.description !== undefined) data.description = payload.description;
  if (payload.order !== undefined) data.order = payload.order;
  if (payload.dwellTimeAtStopMins !== undefined) data.dwellTimeAtStopMins = payload.dwellTimeAtStopMins;
  if (payload.timeToNextStopMins !== undefined) data.timeToNextStopMins = payload.timeToNextStopMins;
  if (payload.addressId !== undefined) data.addressId = payload.addressId;
  if (payload.coordinates !== undefined) {
    data.address = {
      coordinates: payload.coordinates,
      label: payload.label || payload.name || '',
    };
  }

  const body = {
    type: 'updateroutestop',
    version: '1',
    aclConfig: { resource: 'route-stops', operation: 'update' },
    aclContext: 'fixed_route',
    data,
  };

  const response = await makeRequestWithRetry(
    fixedRouteApiUrl,
    'POST',
    headers,
    undefined,
    body,
    5,
    1000,
    environment,
  );
  return parseRouteStopCommandResponse(response, 'update');
}

/**
 * Remove a stop from a route version.
 */
export async function deleteRouteStop(
  jwtToken: string,
  productId: string,
  payload: { routeId: string; versionId: string; routeStopId: string },
  environment: Environment = 'UAT',
): Promise<UATRouteStopCommandResponse> {
  if (!jwtToken || !jwtToken.trim()) throw new Error('JWT token is required');
  if (!payload.routeId) throw new Error('routeId is required');
  if (!payload.versionId) throw new Error('versionId is required');
  if (!payload.routeStopId) throw new Error('routeStopId is required');

  const { fixedRouteApiUrl } = getApiUrls(environment);
  const headers = createHeaders(jwtToken, productId, environment);

  const body = {
    type: 'deleteroutestop',
    version: '1',
    aclConfig: { resource: 'route-stops', operation: 'delete' },
    aclContext: 'fixed_route',
    data: {
      routeId: payload.routeId,
      versionId: payload.versionId,
      routeStopId: payload.routeStopId,
    },
  };

  const response = await makeRequestWithRetry(
    fixedRouteApiUrl,
    'POST',
    headers,
    undefined,
    body,
    5,
    1000,
    environment,
  );
  return parseRouteStopCommandResponse(response, 'delete');
}

/**
 * Reorder the stops on a route version.
 */
export async function orderRouteStops(
  jwtToken: string,
  productId: string,
  payload: { routeId: string; versionId: string; orderedRouteStopIds: string[] },
  environment: Environment = 'UAT',
): Promise<UATRouteStopCommandResponse> {
  if (!jwtToken || !jwtToken.trim()) throw new Error('JWT token is required');
  if (!payload.routeId) throw new Error('routeId is required');
  if (!payload.versionId) throw new Error('versionId is required');
  if (!Array.isArray(payload.orderedRouteStopIds) || payload.orderedRouteStopIds.length === 0) {
    throw new Error('orderedRouteStopIds must be a non-empty array');
  }

  const { fixedRouteApiUrl } = getApiUrls(environment);
  const headers = createHeaders(jwtToken, productId, environment);

  const body = {
    type: 'orderroutestops',
    version: '1',
    aclConfig: { resource: 'route-stops', operation: 'update' },
    aclContext: 'fixed_route',
    data: {
      routeId: payload.routeId,
      versionId: payload.versionId,
      orderedRouteStopIds: payload.orderedRouteStopIds,
    },
  };

  const response = await makeRequestWithRetry(
    fixedRouteApiUrl,
    'POST',
    headers,
    undefined,
    body,
    5,
    1000,
    environment,
  );
  return parseRouteStopCommandResponse(response, 'reorder');
}

/**
 * Fetch existing operations solutions from UAT or Production for duplicate detection
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID
 * @param date - Service date in YYYY-MM-DD format
 * @param timezone - Timezone string (e.g., "Australia/Sydney")
 * @param environment - Environment to fetch from ('UAT' or 'Production'), defaults to 'UAT'
 * @returns Promise resolving to operations solutions response
 */
export async function fetchOperationsSolutions(
  jwtToken: string,
  productId: string,
  date: string,
  timezone: string,
  environment: Environment = 'UAT'
): Promise<UATOperationsSolutionsResponse> {
  // Use base URL without query params - backend proxy will add them
  const params: Record<string, string> = {
    type: 'getoperationssolutionsall',
    version: '1',
    resource: 'shift-instances',
    operation: 'list',
    aclContext: 'fixed_route',
    date,
    timezone,
  };

  const { fixedRouteQueryUrl } = getApiUrls(environment);

  const response = await makeRequestWithRetry(
    fixedRouteQueryUrl,
    'GET',
    createHeaders(jwtToken, productId, environment),
    params,
    undefined,
    5,
    1000,
    environment
  );

  if (response.status !== 200) {
    const errorData: UATErrorResponse = typeof response.data === 'object' && response.data
      ? response.data
      : {
          status: response.status,
          message: `HTTP ${response.status}`,
        };

    throw new Error(`Failed to fetch operations solutions: ${errorData.message}`);
  }

  return response.data as UATOperationsSolutionsResponse;
}

/**
 * Response from getroutelist endpoint
 */
export interface UATRouteListResponse {
  status: number;
  data: {
    totalEntries: number;
    entries: Array<{
      id: string;
      label: string; // Route name
      description: string;
      colour: string;
      latestVersion: string; // e.g., "Version 1", "Version 2"
      lastUpdatedAt: string; // ISO datetime string
      lastUpdatedBy: string;
    }>;
  };
}

/**
 * Fetch route list from UAT or Production using getroutelist endpoint
 * 
 * This is more efficient than getoperationssolutionsall as it only returns route metadata,
 * not full operations data. Supports pagination and sorting.
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID
 * @param offset - Pagination offset (default: 0)
 * @param limit - Pagination limit (default: 1000 to get all routes)
 * @param sortBy - Sort field (default: 'label')
 * @param sortOrder - Sort order 'ASC' or 'DESC' (default: 'DESC')
 * @param environment - Environment ('UAT' or 'Production')
 * @returns Promise resolving to route list response
 */
export async function fetchRouteList(
  jwtToken: string,
  productId: string,
  offset: number = 0,
  limit: number = 1000,
  sortBy: string = 'label',
  sortOrder: 'ASC' | 'DESC' = 'DESC',
  environment: Environment = 'UAT'
): Promise<UATRouteListResponse> {
  if (!jwtToken || !jwtToken.trim()) {
    throw new Error('JWT token is required');
  }

  const params: Record<string, string> = {
    type: 'getroutelist',
    version: '1',
    resource: 'routes',
    operation: 'list',
    aclContext: 'fixed_route',
    sortBy,
    sortOrder,
    offset: offset.toString(),
    limit: limit.toString(),
  };

  const { fixedRouteQueryUrl } = getApiUrls(environment);

  const response = await makeRequestWithRetry(
    fixedRouteQueryUrl,
    'GET',
    createHeaders(jwtToken, productId, environment),
    params,
    undefined,
    5,
    1000,
    environment
  );

  if (response.status !== 200) {
    const errorData: UATErrorResponse = typeof response.data === 'object' && response.data
      ? response.data
      : {
          status: response.status,
          message: `HTTP ${response.status}`,
        };

    throw new Error(`Failed to fetch route list: ${errorData.message}`);
  }

  return response.data as UATRouteListResponse;
}

/**
 * Fetch routes from target network for comparison during upload
 * 
 * Uses getoperationssolutionsall endpoint to ensure route IDs match with download functionality.
 * This ensures that routes displayed in the database panel match the routes available for download.
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product/network ID
 * @param date - Service date (YYYY-MM-DD format)
 * @param timezone - Timezone (e.g., "Australia/Sydney")
 * @param environment - Environment ('UAT' or 'Production')
 * @returns Simplified route list with id, name, version, stopCount, lastModified
 */
export async function fetchRoutesForComparison(
  jwtToken: string,
  productId: string,
  date: string,
  timezone: string,
  environment: Environment = 'UAT'
): Promise<Array<{
  id: string;
  name: string;
  version?: string;
  stopCount: number;
  lastModified?: string;
  stops: Array<{
    id: string;
    order: number;
    timeToNextStop: number;
    timeSpentAtStop: number;
  }>;
}>> {
  // Use fetchOperationsSolutions to ensure route IDs match with download functionality
  // This ensures routes displayed in the database panel are the same routes available for download
  const operationsSolutions = await fetchOperationsSolutions(jwtToken, productId, date, timezone, environment);
  
  // Extract routes using the same logic as extractUATRoutes
  const routes: Array<{
    id: string;
    name: string;
    version?: string;
    stopCount: number;
    lastModified?: string;
    stops: Array<{
      id: string;
      order: number;
      timeToNextStop: number;
      timeSpentAtStop: number;
    }>;
  }> = [];

  if (!operationsSolutions.data?.entries) {
    return routes;
  }

  // Track unique routes by route ID (same logic as extractUATRoutes)
  const routeMap = new Map<string, {
    routeId: string;
    routeName: string;
    stops: Array<{
      id: string;
      order: number;
      timeToNextStop: number;
      timeSpentAtStop: number;
    }>;
  }>();

  // Iterate through all operations solutions
  for (const entry of operationsSolutions.data.entries) {
    if (!entry.shiftSolutions) continue;

    // Iterate through all shift solutions
    for (const shiftSolution of entry.shiftSolutions) {
      if (!shiftSolution.route) continue;

      const route = shiftSolution.route;
      const routeId = route.id;

      if (!routeMap.has(routeId)) {
        routeMap.set(routeId, {
          routeId,
          routeName: route.label || `Route ${routeId}`,
          stops: [],
        });
      }

      const routeData = routeMap.get(routeId)!;

      // Extract stops from route.stops[]
      if (route.stops && route.stops.length > 0) {
        for (let i = 0; i < route.stops.length; i++) {
          const stop = route.stops[i];
          if (stop.id && !routeData.stops.some((s) => s.id === stop.id)) {
            routeData.stops.push({
              id: stop.id,
              order: stop.order ?? i,
              timeToNextStop: stop.timeToNextStop ?? 0,
              timeSpentAtStop: stop.timeSpentAtStop ?? 0,
            });
          }
        }
      }
    }
  }

  // Convert to simplified format
  for (const routeData of Array.from(routeMap.values())) {
    routes.push({
      id: routeData.routeId,
      name: routeData.routeName,
      stopCount: routeData.stops.length,
      stops: routeData.stops,
    });
  }

  return routes;
}

/**
 * Fetch all routes from UAT or Production using getroutelist endpoint
 * 
 * This function returns ALL routes regardless of service date, making it suitable
 * for displaying routes that may not have service dates assigned yet.
 * 
 * Note: This endpoint doesn't return stop information, so stopCount will be 0
 * and stops array will be empty. For full route details with stops, use fetchRoutesForComparison.
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID
 * @param environment - Environment ('UAT' or 'Production')
 * @returns Simplified route list with id, name, version, stopCount (0), lastModified
 */
export async function fetchAllRoutes(
  jwtToken: string,
  productId: string,
  environment: Environment = 'UAT'
): Promise<Array<{
  id: string;
  name: string;
  version?: string;
  stopCount: number;
  lastModified?: string;
  stops: Array<{
    id: string;
    order: number;
    timeToNextStop: number;
    timeSpentAtStop: number;
  }>;
}>> {
  const routeListResponse = await fetchRouteList(
    jwtToken,
    productId,
    0, // offset
    1000, // limit - get all routes
    'label', // sortBy
    'DESC', // sortOrder
    environment
  );

  if (!routeListResponse.data?.entries) {
    return [];
  }

  // Convert UATRouteListResponse entries to SimplifiedRoute format
  return routeListResponse.data.entries.map((entry) => ({
    id: entry.id,
    name: entry.label,
    version: entry.latestVersion, // e.g., "Version 1", "Version 2"
    stopCount: 0, // Route list doesn't include stop count
    lastModified: entry.lastUpdatedAt,
    stops: [], // Route list doesn't include stops
  }));
}

/**
 * Response from getroutestoplist endpoint
 */
export interface UATRouteStopListResponse {
  status: number;
  data: {
    totalEntries: number;
    entries: Array<{
      id: string;
      label: string; // Stop name
      description: string | null;
      order: number;
      latitude: number;
      longitude: number;
    }>;
  };
}

/**
 * Fetch stops for a specific route using getroutestoplist endpoint
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID
 * @param routeId - Route UUID
 * @param offset - Pagination offset (default: 0)
 * @param limit - Pagination limit (default: 1000 to get all stops)
 * @param sortBy - Sort field (default: 'order')
 * @param sortOrder - Sort order 'ASC' or 'DESC' (default: 'ASC')
 * @param environment - Environment ('UAT' or 'Production')
 * @returns Promise resolving to route stop list response
 */
export async function fetchRouteStopList(
  jwtToken: string,
  productId: string,
  routeId: string,
  offset: number = 0,
  limit: number = 1000,
  sortBy: string = 'order',
  sortOrder: 'ASC' | 'DESC' = 'ASC',
  environment: Environment = 'UAT'
): Promise<UATRouteStopListResponse> {
  if (!jwtToken || !jwtToken.trim()) {
    throw new Error('JWT token is required');
  }
  if (!routeId || !routeId.trim()) {
    throw new Error('Route ID is required');
  }

  const params: Record<string, string> = {
    type: 'getroutestoplist',
    version: '1',
    resource: 'routes',
    operation: 'view',
    aclContext: 'fixed_route',
    routeId: routeId.trim(),
    sortBy,
    sortOrder,
    offset: offset.toString(),
    limit: limit.toString(),
  };

  const { fixedRouteQueryUrl } = getApiUrls(environment);

  const response = await makeRequestWithRetry(
    fixedRouteQueryUrl,
    'GET',
    createHeaders(jwtToken, productId, environment),
    params,
    undefined,
    5,
    1000,
    environment
  );

  if (response.status !== 200) {
    const errorData: UATErrorResponse = typeof response.data === 'object' && response.data
      ? response.data
      : {
          status: response.status,
          message: `HTTP ${response.status}`,
        };

    throw new Error(`Failed to fetch route stop list: ${errorData.message}`);
  }

  return response.data as UATRouteStopListResponse;
}

/**
 * Fetch On-Demand (ODB) fleets from UAT or Production
 * 
 * Uses the same endpoint as Fixed Route fleets but with aclContext: 'operations' for on-demand data.
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID
 * @param date - Service date (YYYY-MM-DD format)
 * @param timezone - Timezone (e.g., 'America/New_York')
 * @param networkId - Optional network ID (UUID string, not dbId)
 * @param environment - Environment to fetch from ('UAT' or 'Production'), defaults to 'UAT'
 * @returns Promise resolving to operations solutions response
 */
export async function fetchODBFleets(
  jwtToken: string,
  productId: string,
  date: string,
  timezone: string,
  networkId?: string,
  environment: Environment = 'UAT'
): Promise<UATShiftSolutionsResponse> {
  // Use getshiftsolutions endpoint for on-demand fleets
  // This is different from FR fleets which use getoperationssolutionsall
  // Note: timezone is not used in this endpoint, only date and networkId
  const params: Record<string, string> = {
    type: 'getshiftsolutions',
    version: '1',
    resource: 'shifts',
    operation: 'view',
    aclContext: 'operations',
    date,
  };

  // Add networkId if provided (required for the endpoint - must be UUID string, not dbId)
  if (networkId) {
    params.networkId = networkId;
  } else {
    console.warn('[fetchUATODBFleets] No networkId provided - API call may fail');
  }

  const { opsApiUrl } = getApiUrls(environment);
  console.log('[fetchUATODBFleets] Making API request with params:', params);
  console.log('[fetchUATODBFleets] URL:', opsApiUrl);

  const response = await makeRequestWithRetry(
    opsApiUrl, // Use ops API URL for on-demand data
    'GET',
    createHeaders(jwtToken, productId, environment),
    params,
    undefined,
    5,
    1000,
    environment
  );

  console.log('[fetchUATODBFleets] API response:', {
    status: response.status,
    hasData: !!response.data,
    dataKeys: response.data ? Object.keys(response.data) : [],
    hasDataData: !!response.data?.data,
    dataDataKeys: response.data?.data ? Object.keys(response.data.data) : [],
  });

  if (response.status !== 200) {
    const errorData: UATErrorResponse = typeof response.data === 'object' && response.data
      ? response.data
      : {
          status: response.status,
          message: `HTTP ${response.status}`,
        };

    const errorMessage = errorData.message || `HTTP ${response.status}`;
    console.error('[fetchUATODBFleets] API error:', {
      status: response.status,
      errorData,
      response: response.data,
    });
    throw new Error(`Failed to fetch ODB fleets: ${errorMessage}`);
  }

  // Log successful response for debugging
  console.log('[fetchUATODBFleets] Successfully fetched ODB fleets:', {
    hasData: !!response.data,
    hasSolutions: !!response.data?.data?.solutions,
    solutionsCount: response.data?.data?.solutions?.length || 0,
    fullResponse: response.data,
  });

  return response.data as UATShiftSolutionsResponse;
}

// ---------------------------------------------------------------------------
// Network vehicle master (date-free) — W7 getvehicles endpoint
// ---------------------------------------------------------------------------

/**
 * A single vehicle entry from the network vehicle master list.
 * Date-free: represents a vehicle registered to the network, not a scheduled assignment.
 */
export interface OpsNetworkVehicle {
  /** Ops vehicle UUID — maps to `vehicleId` in shift assignment payloads. */
  id: string;
  /** Human-readable label, e.g. "Bus 12" or vehicle registration. */
  label: string;
}

/**
 * Response shape from the `getvehicles` Ops API query.
 * The `vehicles` array is the authoritative master list for the network.
 */
export interface OpsNetworkVehicleMasterResponse {
  status: number;
  data: {
    vehicles: OpsNetworkVehicle[];
  };
}

export interface NetworkVehicleLoadErrorContext {
  environment: Environment;
  networkId: string;
  productName?: string;
}

/**
 * User-facing message when the Ops `getvehicles` call fails.
 */
export function buildNetworkVehicleLoadErrorMessage(
  status: number,
  errorData: UATErrorResponse,
  context: NetworkVehicleLoadErrorContext
): string {
  const productPart = context.productName ? ` for “${context.productName}”` : '';
  const envLabel = context.environment;
  const serverDetail =
    errorData.message?.trim() && !/^HTTP \d+$/i.test(errorData.message.trim())
      ? errorData.message.trim()
      : undefined;
  const numericNetworkId = /^\d+$/.test(context.networkId);

  if (numericNetworkId) {
    return [
      `Could not load vehicles from Ops ${envLabel}${productPart}.`,
      'A numeric network ID was sent instead of the network UUID the Ops API expects.',
      'Click Load networks, re-select the product, then try Load vehicles again.',
      serverDetail ? `Ops response: ${serverDetail}` : undefined,
    ]
      .filter(Boolean)
      .join(' ');
  }

  if (status === 500) {
    return [
      `Could not load the vehicle list from Ops ${envLabel}${productPart}.`,
      'The Ops server returned an internal error (HTTP 500)—usually a temporary server issue or a problem with that network’s fleet data, not Atlas.',
      'Try another product/network, wait and retry, or ask Ops support if it persists.',
      serverDetail ? `Ops response: ${serverDetail}` : undefined,
    ]
      .filter(Boolean)
      .join(' ');
  }

  if (status === 401 || status === 403) {
    return [
      `Ops ${envLabel} rejected the vehicle request${productPart} (HTTP ${status}).`,
      `Confirm your JWT is for ${envLabel} and has permission to view fleet/vehicles.`,
      serverDetail ? `Ops response: ${serverDetail}` : undefined,
    ]
      .filter(Boolean)
      .join(' ');
  }

  if (status === 404) {
    return [
      `No vehicle list found${productPart} on Ops ${envLabel} (HTTP 404).`,
      'The network may have no registered vehicles, or the product/network pairing may be wrong.',
      serverDetail ? `Ops response: ${serverDetail}` : undefined,
    ]
      .filter(Boolean)
      .join(' ');
  }

  return [
    `Could not load vehicles from Ops ${envLabel}${productPart} (HTTP ${status}).`,
    serverDetail ? `Ops response: ${serverDetail}` : undefined,
  ]
    .filter(Boolean)
    .join(' ');
}

export interface FixedRouteVehicleListItem {
  id: string;
  rego?: string;
  name?: string;
  label?: string;
  status?: string;
  seats?: Array<{
    amount?: number;
    capacityName?: string;
  }>;
}

export interface FixedRouteVehicleListResponse {
  status: number;
  data: {
    entry?: FixedRouteVehicleListItem[];
    vehicles?: FixedRouteVehicleListItem[];
    items?: FixedRouteVehicleListItem[];
  };
  total?: number;
  offset?: number;
  limit?: number;
}

/**
 * Fetches fixed-route vehicle list (primary endpoint for postcode mapping).
 *
 * Endpoint:
 * `/context/fixed-route/q?type=getvehiclelist&version=1&resource=vehicles&operation=list&aclContext=fixed_route`
 */
export async function fetchFixedRouteVehicleList(
  jwtToken: string,
  productId: string,
  environment: Environment = 'UAT',
  offset: number = 0,
  limit: number = 500
): Promise<FixedRouteVehicleListResponse> {
  if (!jwtToken || !jwtToken.trim()) throw new Error('JWT token is required');
  if (!productId || !productId.trim()) throw new Error('productId is required');

  const params: Record<string, string> = {
    type: 'getvehiclelist',
    version: '1',
    resource: 'vehicles',
    operation: 'list',
    aclContext: 'fixed_route',
    sortBy: 'rego',
    sortOrder: 'ASC',
    offset: String(offset),
    limit: String(limit),
  };

  const { fixedRouteQueryUrl } = getApiUrls(environment);
  const response = await makeRequestWithRetry(
    fixedRouteQueryUrl,
    'GET',
    createHeaders(jwtToken, productId, environment),
    params,
    undefined,
    3,
    1000,
    environment
  );

  if (response.status !== 200) {
    const errorData: UATErrorResponse =
      typeof response.data === 'object' && response.data
        ? response.data
        : { status: response.status, message: `HTTP ${response.status}` };
    throw new Error(`Failed to fetch fixed-route vehicle list: ${errorData.message || `HTTP ${response.status}`}`);
  }

  return {
    status: response.status,
    data: response.data?.data || response.data || { entry: [] },
    total: response.data?.total,
    offset: response.data?.offset,
    limit: response.data?.limit,
  };
}

/**
 * Fetches the date-free vehicle master list for a given Ops network.
 *
 * Uses the `getvehicles` query type against the Ops query endpoint, which
 * returns all vehicles registered to the network regardless of schedule date.
 * This is the W7 dedicated vehicle-list endpoint replacing the prior
 * schedule-scraping approach.
 *
 * @param jwtToken - JWT authentication token
 * @param productId - Ops product UUID
 * @param networkId - Ops network UUID (string, not dbId)
 * @param environment - "UAT" or "Production"
 * @returns Network vehicle master list response
 */
export async function fetchNetworkVehicleMaster(
  jwtToken: string,
  productId: string,
  networkId: string,
  environment: Environment = 'UAT',
  productName?: string
): Promise<OpsNetworkVehicleMasterResponse> {
  if (!jwtToken || !jwtToken.trim()) throw new Error('JWT token is required');
  if (!productId || !productId.trim()) throw new Error('productId is required');
  if (!networkId || !networkId.trim()) throw new Error('networkId is required');

  const params: Record<string, string> = {
    type: 'getvehicles',
    version: '1',
    resource: 'fleet',
    operation: 'view',
    aclContext: 'operations',
    networkId,
  };

  const { opsApiUrl } = getApiUrls(environment);

  const response = await makeRequestWithRetry(
    opsApiUrl,
    'GET',
    createHeaders(jwtToken, productId, environment),
    params,
    undefined,
    3,
    1000,
    environment
  );

  if (response.status !== 200) {
    const errorData: UATErrorResponse =
      typeof response.data === 'object' && response.data
        ? response.data
        : { status: response.status, message: `HTTP ${response.status}` };
    throw new Error(
      buildNetworkVehicleLoadErrorMessage(response.status, errorData, {
        environment,
        networkId,
        productName,
      })
    );
  }

  return response.data as OpsNetworkVehicleMasterResponse;
}

/**
 * Fetch network settings from UAT or Production
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID (optional, for filtering)
 * @param environment - Environment to fetch from ('UAT' or 'Production'), defaults to 'UAT'
 * @returns Promise resolving to network settings response
 */
export async function fetchNetworkSettings(
  jwtToken: string,
  productId?: string,
  environment: Environment = 'UAT'
): Promise<UATNetworkSettingsResponse> {
  const params: Record<string, string> = {
    type: 'getnetworksettings',
    version: '1',
    disableAccessPermissionChecks: 'true',
    aclContext: 'fixed_route',
  };

  const { fixedRouteQueryUrl } = getApiUrls(environment);

  const response = await makeRequestWithRetry(
    fixedRouteQueryUrl,
    'GET',
    createHeaders(jwtToken, productId, environment),
    params,
    undefined,
    5,
    1000,
    environment
  );

  if (response.status !== 200) {
    const errorData: UATErrorResponse = typeof response.data === 'object' && response.data
      ? response.data
      : {
          status: response.status,
          message: `HTTP ${response.status}`,
        };

    throw new Error(`Failed to fetch network settings: ${errorData.message}`);
  }

  return response.data as UATNetworkSettingsResponse;
}

/**
 * Fetch stops list from UAT or Production
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID (optional, for filtering)
 * @param offset - Pagination offset (default: 0)
 * @param limit - Pagination limit (default: 20)
 * @param environment - Environment to fetch from ('UAT' or 'Production'), defaults to 'UAT'
 * @returns Promise resolving to stops list response
 */
export async function fetchStopsList(
  jwtToken: string,
  productId?: string,
  offset: number = 0,
  limit: number = 20,
  environment: Environment = 'UAT'
): Promise<UATStopsListResponse> {
  const params: Record<string, string> = {
    type: 'getstopslist',
    version: '1',
    resource: 'stops',
    operation: 'list',
    aclContext: 'fixed_route',
    sortBy: 'id',
    sortOrder: 'DESC',
    offset: offset.toString(),
    limit: limit.toString(),
  };

  const { fixedRouteQueryUrl } = getApiUrls(environment);

  const response = await makeRequestWithRetry(
    fixedRouteQueryUrl,
    'GET',
    createHeaders(jwtToken, productId, environment),
    params,
    undefined,
    5,
    1000,
    environment
  );

  if (response.status === 200) {
    return {
      status: response.status,
      data: response.data?.data || response.data || { entry: [] },
      total: response.data?.total,
      offset: response.data?.offset,
      limit: response.data?.limit,
    };
  }

  throw new Error(`Failed to fetch stops list: ${response.status}`);
}

/**
 * UAT Hub data structure (from service settings)
 */
export interface UATHub {
  addressId: string;        // UUID identifier
  icon: string;             // Icon type (e.g., "circle")
  name: string;             // Hub name (e.g., "A/Rampa", "APADEV")
  label: string;            // Full address label
  placeId: string;          // Google Places ID
  latitude: number;         // Latitude coordinate
  longitude: number;        // Longitude coordinate
  snapRadius: number;       // Snap radius in meters (e.g., 20)
  type: string;             // Type identifier (e.g., "poi")
}

/**
 * UAT Geofence data structure (from service settings)
 * Exact structure to be determined from API response
 */
export interface UATGeofence {
  id?: string;
  name?: string;
  type?: string;
  geometry?: {
    type: string;           // "Polygon", "Circle", etc.
    coordinates?: any;       // Geometry coordinates (format depends on type)
    center?: {
      latitude: number;
      longitude: number;
    };
    radius?: number;        // For circles
  };
  [key: string]: any;        // Allow additional fields from API
}

/**
 * Service Settings Response from UAT
 * Proxy returns: { status: 200, data: { status: 200, data: { serviceSettings: [...] } } }
 * After fetchUATServiceSettings returns response.data: { status: 200, data: { serviceSettings: [...] } }
 */
/**
 * Capacity type from service settings
 */
export interface UATCapacityType {
  name: string;
  iconName: string | null;
  displayOrder: number;
}

/**
 * Capacity configuration from service settings
 */
export interface UATCapacity {
  id: string;
  onceOffTime: number;
  timePerUnit: number;
  movable: boolean;
  isDependant: boolean;
  type: UATCapacityType;
}

/**
 * On-Demand (ODB) Service Information from UAT
 * Extracted from service settings response
 */
export interface UATODBServiceInformation {
  id: string;
  name: string;
  description?: string;
  startDate: string;
  endDate: string | null;
  bookingStartDate?: string;
  serviceType: string; // e.g., 'doorToDoor'
  serviceFlags: {
    hubsEnabled?: boolean;
    geofencesEnabled?: boolean;
    breaksEnabled?: boolean;
    zonesEnabled?: boolean;
    isHubToHub?: boolean;
    [key: string]: any;
  };
  timezone: string;
  rideLockTimeOffset: number;
  maxCompanions: number;
  networkId: string;
  capacities?: UATCapacity[];
  solutions?: Array<{
    start: string;
    end: string;
    [key: string]: any;
  }>;
  [key: string]: any; // Allow additional fields from API
}

export interface UATServiceSettingsResponse {
  status: number;
  data: {
    status?: number;  // May be present if proxy returns full response
    data?: {
      serviceSettings?: Array<{
        id?: string;
        name?: string;
        description?: string;
        startDate?: string;
        endDate?: string | null;
        bookingStartDate?: string;
        serviceType?: string;
        serviceFlags?: any;
        timezone?: string;
        rideLockTimeOffset?: number;
        maxCompanions?: number;
        networkId?: string;
        solutions?: Array<{ start: string; end: string }>;
        geofences?: UATGeofence[];
        hubs?: UATHub[];
        capacities?: any[];
        [key: string]: any;
      }>;
      [key: string]: any;
    };
    // Direct access (if proxy strips one level): data.serviceSettings
    serviceSettings?: Array<{
      id?: string;
      name?: string;
      geofences?: UATGeofence[];
      hubs?: UATHub[];
      [key: string]: any;
    }>;
    geofences?: UATGeofence[];  // Fallback: direct geofences (if API structure differs)
    hubs?: UATHub[];             // Fallback: direct hubs (if API structure differs)
    [key: string]: any;          // Allow additional fields from API
  };
}

/**
 * UAT Seat data (for rides)
 */
export interface UATSeat {
  id: string;
  seatType: 'standard' | 'wheelchair';
  concessionType: string | null;
  luggage: any[];
}

/**
 * UAT Address data (from ride segments)
 */
export interface UATAddress {
  label: string;
  placeId?: string;
  position: {
    latitude: number;
    longitude: number;
  };
  addressId: string;
  addressDbId: number;
}

/**
 * UAT Ride Segment data (pickup/dropoff)
 */
export interface UATRideSegment {
  activity: 'pickup' | 'dropoff';
  address: UATAddress;
  description?: string;
  time: string | null;  // ISO timestamp or null
}

/**
 * UAT Ride Entry data from getridelist endpoint
 */
export interface UATRideEntry {
  id: string;                  // UUID
  dbId?: number;                // Database ID (may not be in list response)
  mergedId?: string | null;     // Merged ride ID if applicable
  type?: string;                // Ride type: "ARRIVAL_FIXED", "DEPARTURE_FIXED", etc.
  createdAt: string;           // ISO timestamp
  createdBy?: string;           // User UUID who created the ride
  updatedAt?: string;           // ISO timestamp
  departureTime?: string;       // ISO timestamp
  arrivalTime?: string;         // ISO timestamp (may be empty string)
  status: string;              // Status: "merged", "cancelled", etc.
  network?: {
    id: string;                // Network UUID
    name: string;               // Network name
  };
  unassignedReason?: string | null;
  occupiedSeats?: UATSeat[];    // Array of occupied seats
  owner?: {
    id: string;                 // User UUID
    displayId: number;          // User display ID
    firstName: string;
    lastName: string;
  };
  user?: {
    id: string;                 // User UUID
    displayId: number;          // User display ID
    firstName: string;
    lastName: string;
    mobile?: string;
    hasPaymentSource?: boolean;
  };
  tripPassengers: UATSeat[];    // Array of trip passengers
  displayId: number;            // Ride display ID
  rideType: string;             // "on-demand", etc.
  // Segments contain pickup/dropoff coordinates and addresses
  segments?: UATRideSegment[];  // Array of segments (pickup, dropoff)
  time?: string;                // ISO timestamp (departure time)
  tripType?: string;            // "leaveAt", "arriveBy", etc.
  journey?: boolean;            // Whether this is a journey
  [key: string]: any;           // Allow additional fields from API
}

/**
 * UAT Detailed Ride Entry (from getridedetailed endpoint)
 */
export interface UATRideDetailedEntry extends UATRideEntry {
  segments: UATRideSegment[];   // Always present in detailed response
  rideCancelLimit?: string;     // ISO timestamp
  passengerNotes?: any[];        // Array of passenger notes
  schedule?: any;                // Schedule information
  vehicle?: any;                 // Vehicle assignment
  charge?: {
    amount: number;
    amountFormatted: string;
    total: number | null;
    totalFormatted: string;
    isPaid: boolean;
    currency: string;
    currencyLocale: string;
    ledgerEntries: any[];
    partialPayment: boolean;
    amountPaid: number;
  };
  rideChargeOverride?: any;
}

/**
 * Response from getridelist endpoint
 */
export interface UATRidesResponse {
  status: number;
  data: {
    totalEntries: number;      // Total number of rides available
    entries: UATRideEntry[];   // Array of ride entries
  };
}

/**
 * Response from getridedetailed endpoint
 */
export interface UATRideDetailedResponse {
  status: number;
  data: {
    entry: UATRideDetailedEntry;  // Single detailed ride entry
  };
}

/**
 * Fetch service settings from UAT (includes geofences and hubs)
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @returns Promise resolving to service settings response
 */
export async function fetchServiceSettings(
  jwtToken: string,
  productId: string,
  startDate: string,
  endDate: string,
  environment: Environment = 'UAT'
): Promise<UATServiceSettingsResponse> {
  const params: Record<string, string> = {
    type: 'getservicesettings',
    version: '1',
    disableAccessPermissionChecks: 'true',
    aclContext: 'operations',
    startDate,
    endDate,
  };

  const { opsApiUrl } = getApiUrls(environment);

  const response = await makeRequestWithRetry(
    opsApiUrl,
    'GET',
    createHeaders(jwtToken, productId, environment),
    params,
    undefined,
    5,
    1000,
    environment
  );

  if (response.status !== 200) {
    const errorData: UATErrorResponse = typeof response.data === 'object' && response.data
      ? response.data
      : {
          status: response.status,
          message: `HTTP ${response.status}`,
        };

    throw new Error(`Failed to fetch service settings: ${errorData.message}`);
  }

  // The proxy may return the full response or just the data portion
  // Log the structure to help debug
  console.log('[fetchUATServiceSettings] Raw response structure:', {
    hasStatus: 'status' in response.data,
    hasData: 'data' in response.data,
    hasDataData: 'data' in (response.data as any)?.data,
    keys: Object.keys(response.data || {}),
  });
  
  return response.data as UATServiceSettingsResponse;
}

/**
 * Fetch geofences from UAT or Production service settings
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @param environment - Environment to fetch from ('UAT' or 'Production'), defaults to 'UAT'
 * @returns Promise resolving to array of geofences
 */
export async function fetchGeofences(
  jwtToken: string,
  productId: string,
  startDate: string,
  endDate: string,
  environment: Environment = 'UAT'
): Promise<UATGeofence[]> {
  const serviceSettings = await fetchServiceSettings(jwtToken, productId, startDate, endDate, environment);
  
  console.log('[fetchGeofences] Service settings response structure:', {
    hasData: !!serviceSettings.data,
    hasDataData: !!serviceSettings.data?.data,
    hasServiceSettings: !!serviceSettings.data?.data?.serviceSettings,
    serviceSettingsCount: serviceSettings.data?.data?.serviceSettings?.length || 0,
    fullStructure: JSON.stringify(serviceSettings).substring(0, 500), // First 500 chars for debugging
  });
  
  // Extract geofences from response
  // Proxy response structure: { status: 200, data: { status: 200, data: { serviceSettings: [...] } } }
  // After fetchUATServiceSettings returns response.data: { status: 200, data: { serviceSettings: [...] } }
  // So we need: serviceSettings.data.serviceSettings[].geofences[]
  const allGeofences: UATGeofence[] = [];
  
  // Check for serviceSettings array (primary structure)
  // Try both possible paths: data.data.serviceSettings (if proxy returns full response) or data.serviceSettings (if proxy returns just data)
  const serviceSettingsArray = 
    (serviceSettings.data as any)?.data?.serviceSettings ||  // Nested: data.data.serviceSettings
    serviceSettings.data?.serviceSettings ||                 // Direct: data.serviceSettings
    (serviceSettings as any).serviceSettings;                // Fallback: top-level serviceSettings
  
  if (serviceSettingsArray && Array.isArray(serviceSettingsArray)) {
    console.log('[fetchUATGeofences] Processing serviceSettings array, count:', serviceSettingsArray.length);
    for (const setting of serviceSettingsArray) {
      if (setting.geofences && Array.isArray(setting.geofences)) {
        console.log(`[fetchUATGeofences] Found ${setting.geofences.length} geofences in service setting "${setting.name || setting.id}"`);
        allGeofences.push(...setting.geofences);
      } else {
        console.log(`[fetchUATGeofences] No geofences in service setting "${setting.name || setting.id}"`);
      }
    }
  } else {
    console.log('[fetchUATGeofences] No serviceSettings array found, checking fallback locations');
  }
  
  // Fallback: check for direct geofences (if API structure differs)
  if (allGeofences.length === 0) {
    console.log('[fetchUATGeofences] Trying fallback: checking for direct geofences');
    const directGeofences = serviceSettings.data?.geofences || 
                            serviceSettings.data?.data?.geofences ||
                            [];
    if (Array.isArray(directGeofences)) {
      console.log(`[fetchUATGeofences] Found ${directGeofences.length} direct geofences`);
      allGeofences.push(...directGeofences);
    }
  }
  
  console.log(`[fetchUATGeofences] Extracted ${allGeofences.length} geofences from service settings`);
  if (allGeofences.length > 0) {
    console.log('[fetchUATGeofences] Sample geofence structure:', {
      id: allGeofences[0].id,
      name: allGeofences[0].name,
      type: allGeofences[0].type,
      hasPoints: !!(allGeofences[0] as any).points,
      hasGeometry: !!allGeofences[0].geometry,
      keys: Object.keys(allGeofences[0]),
    });
  }
  return allGeofences;
}

/**
 * Fetch hubs from UAT service settings
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @returns Promise resolving to array of hubs
 */
export async function fetchHubs(
  jwtToken: string,
  productId: string,
  startDate: string,
  endDate: string,
  environment: Environment = 'UAT'
): Promise<UATHub[]> {
  const serviceSettings = await fetchServiceSettings(jwtToken, productId, startDate, endDate, environment);
  
  console.log('[fetchUATHubs] Service settings response structure:', {
    hasData: !!serviceSettings.data,
    hasDataData: !!serviceSettings.data?.data,
    hasServiceSettings: !!serviceSettings.data?.data?.serviceSettings,
    serviceSettingsCount: serviceSettings.data?.data?.serviceSettings?.length || 0,
  });
  
  // Extract hubs from response
  // Proxy response structure: { status: 200, data: { status: 200, data: { serviceSettings: [...] } } }
  // After fetchUATServiceSettings returns response.data: { status: 200, data: { serviceSettings: [...] } }
  // So we need: serviceSettings.data.serviceSettings[].hubs[]
  const allHubs: UATHub[] = [];
  
  // Check for serviceSettings array (primary structure)
  // Try both possible paths: data.data.serviceSettings (if proxy returns full response) or data.serviceSettings (if proxy returns just data)
  const serviceSettingsArray = 
    (serviceSettings.data as any)?.data?.serviceSettings ||  // Nested: data.data.serviceSettings
    serviceSettings.data?.serviceSettings ||                 // Direct: data.serviceSettings
    (serviceSettings as any).serviceSettings;                // Fallback: top-level serviceSettings
  
  if (serviceSettingsArray && Array.isArray(serviceSettingsArray)) {
    console.log('[fetchUATHubs] Processing serviceSettings array, count:', serviceSettingsArray.length);
    for (const setting of serviceSettingsArray) {
      if (setting.hubs && Array.isArray(setting.hubs)) {
        console.log(`[fetchUATHubs] Found ${setting.hubs.length} hubs in service setting "${setting.name || setting.id}"`);
        allHubs.push(...setting.hubs);
      } else {
        console.log(`[fetchUATHubs] No hubs in service setting "${setting.name || setting.id}"`);
      }
    }
  } else {
    console.log('[fetchUATHubs] No serviceSettings array found, checking fallback locations');
  }
  
  // Fallback: check for direct hubs (if API structure differs)
  if (allHubs.length === 0) {
    console.log('[fetchUATHubs] Trying fallback: checking for direct hubs');
    const directHubs = serviceSettings.data?.hubs || 
                       serviceSettings.data?.data?.hubs ||
                       [];
    if (Array.isArray(directHubs)) {
      console.log(`[fetchUATHubs] Found ${directHubs.length} direct hubs`);
      allHubs.push(...directHubs);
    }
  }
  
  console.log(`[fetchUATHubs] Extracted ${allHubs.length} hubs from service settings`);
  if (allHubs.length > 0) {
    console.log('[fetchUATHubs] Sample hub structure:', {
      addressId: allHubs[0].addressId,
      name: allHubs[0].name,
      type: allHubs[0].type,
      hasCoordinates: typeof allHubs[0].latitude === 'number' && typeof allHubs[0].longitude === 'number',
      keys: Object.keys(allHubs[0]),
    });
  }
  return allHubs;
}

/**
 * Fetch rides from UAT or Production (bookings/ride requests)
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID
 * @param startDate - Start date in YYYY-MM-DD format (optional, for filtering)
 * @param endDate - End date in YYYY-MM-DD format (optional, for filtering)
 * @param offset - Pagination offset (optional, default 0)
 * @param limit - Pagination limit (optional, default 1000 for better performance)
 * @param sortBy - Sort field (optional, default 'departureTime')
 * @param sortOrder - Sort order 'ASC' or 'DESC' (optional, default 'DESC')
 * @param environment - Environment to fetch from ('UAT' or 'Production'), defaults to 'UAT'
 * @returns Promise resolving to rides response
 */
export async function fetchRides(
  jwtToken: string,
  productId: string,
  startDate?: string,
  endDate?: string,
  offset: number = 0,
  limit: number = 1000, // Increased for better performance - fetches more rides per request
  sortBy: string = 'departureTime',
  sortOrder: 'ASC' | 'DESC' = 'DESC',
  environment: Environment = 'UAT'
): Promise<UATRidesResponse> {
  const params: Record<string, string> = {
    type: 'getridelist',
    version: '1',
    resource: 'rides',
    operation: 'list',
    aclContext: 'operations',
    sortBy,
    sortOrder,
    offset: offset.toString(),
    limit: limit.toString(),
  };

  // Add date range parameters if provided
  // NOTE: The API appears to IGNORE these parameters and returns all rides regardless
  // Client-side filtering is required after fetching
  // API accepts YYYY-MM-DD format directly (no ISO conversion needed)
  if (startDate) {
    params.startDate = startDate;
  }
  if (endDate) {
    params.endDate = endDate;
  }

  const { opsApiUrl } = getApiUrls(environment);

  const response = await makeRequestWithRetry(
    opsApiUrl,
    'GET',
    createHeaders(jwtToken, productId, environment),
    params,
    undefined,
    5,
    1000,
    environment
  );

  if (response.status !== 200) {
    const errorData: UATErrorResponse = typeof response.data === 'object' && response.data
      ? response.data
      : {
          status: response.status,
          message: `HTTP ${response.status}`,
        };

    throw new Error(`Failed to fetch rides: ${errorData.message}`);
  }

  // Validate response structure
  if (!response.data) {
    throw new Error(`Invalid response: missing data field`);
  }

  // The API response should have { status, data: { totalEntries, entries } }
  // But response.data is already the API response body, so we need to check its structure
  const apiResponse = response.data;
  
  // Handle different possible response structures
  let ridesResponse: UATRidesResponse;
  if ('status' in apiResponse && 'data' in apiResponse) {
    // Standard structure: { status, data: { totalEntries, entries } }
    ridesResponse = apiResponse as UATRidesResponse;
  } else if ('totalEntries' in apiResponse && 'entries' in apiResponse) {
    // Direct structure: { totalEntries, entries }
    ridesResponse = {
      status: response.status,
      data: apiResponse as { totalEntries: number; entries: UATRideEntry[] }
    };
  } else {
    // Log the actual structure for debugging
    const envName = environment === 'UAT' ? 'UAT' : 'Production';
    console.error(`[fetchRides] ${envName} - Unexpected response structure:`, {
      responseStatus: response.status,
      responseData: apiResponse,
      responseDataKeys: Object.keys(apiResponse || {}),
      responseDataType: typeof apiResponse,
    });
    throw new Error(`Unexpected response structure from ${environment} API. Expected { status, data: { totalEntries, entries } } or { totalEntries, entries }`);
  }

  return ridesResponse;
}

/**
 * Fetch detailed ride information from UAT (includes segments with coordinates)
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID
 * @param rideId - Ride UUID
 * @returns Promise resolving to detailed ride response
 */
export async function fetchRideDetailed(
  jwtToken: string,
  productId: string,
  rideId: string,
  environment: Environment = 'UAT'
): Promise<UATRideDetailedResponse> {
  const params: Record<string, string> = {
    type: 'getridedetailed',
    version: '1',
    resource: 'rides',
    operation: 'view',
    aclContext: 'operations',
    rideId,
  };

  const { opsApiUrl } = getApiUrls(environment);

  const response = await makeRequestWithRetry(
    opsApiUrl,
    'GET',
    createHeaders(jwtToken, productId, environment),
    params,
    undefined,
    5,
    1000,
    environment
  );

  if (response.status !== 200) {
    const errorData: UATErrorResponse = typeof response.data === 'object' && response.data
      ? response.data
      : {
          status: response.status,
          message: `HTTP ${response.status}`,
        };

    throw new Error(`Failed to fetch detailed ride: ${errorData.message}`);
  }

  return response.data as UATRideDetailedResponse;
}

/**
 * Fetch detailed ride information for multiple rides in parallel batches
 * This is more efficient than sequential calls when segments are not in the list response
 * 
 * @param jwtToken - JWT authentication token
 * @param productId - Product ID
 * @param rideIds - Array of ride UUIDs to fetch
 * @param batchSize - Number of rides to fetch in parallel per batch (default 10)
 * @param onProgress - Optional callback for progress updates (current, total)
 * @returns Promise resolving to array of detailed ride entries (may be fewer than rideIds if some fail)
 */
export async function fetchRidesDetailedBatch(
  jwtToken: string,
  productId: string,
  rideIds: string[],
  batchSize: number = 10,
  onProgress?: (current: number, total: number) => void,
  environment: Environment = 'UAT'
): Promise<UATRideDetailedEntry[]> {
  const results: UATRideDetailedEntry[] = [];
  
  // Process in batches to avoid overwhelming the API
  for (let i = 0; i < rideIds.length; i += batchSize) {
    const batch = rideIds.slice(i, i + batchSize);
    
    // Fetch all rides in this batch in parallel
    const batchPromises = batch.map(rideId =>
      fetchRideDetailed(jwtToken, productId, rideId, environment)
        .then(response => response.data.entry)
        .catch(error => {
          console.warn(`[fetchUATRidesDetailedBatch] Failed to fetch ride ${rideId}:`, error);
          return null; // Return null for failed rides
        })
    );
    
    const batchResults = await Promise.all(batchPromises);
    
    // Filter out null results (failed rides)
    const successfulResults = batchResults.filter((entry): entry is UATRideDetailedEntry => entry !== null);
    results.push(...successfulResults);
    
    // Report progress
    if (onProgress) {
      onProgress(results.length, rideIds.length);
    }
  }
  
  console.log(`[fetchUATRidesDetailedBatch] Fetched ${results.length} of ${rideIds.length} rides`);
  return results;
}

// ---------------------------------------------------------------------------
// Route upsert helpers (CAB-90)
//
// `applyrouteversion` is the post-upsert call ops-portal makes (behind a
// confirm modal) to point future shifts at the new version. The upload flow
// invokes it after a successful version bump when the user opts in.
// ---------------------------------------------------------------------------

export interface UATApplyRouteVersionResponse {
  status: number;
  success: boolean;
  error?: string;
}

export async function applyRouteVersionToShifts(
  jwtToken: string,
  productId: string,
  routeVersionToApplyId: string,
  environment: Environment = 'UAT',
): Promise<UATApplyRouteVersionResponse> {
  if (!jwtToken || !jwtToken.trim()) {
    throw new Error('JWT token is required');
  }
  if (!routeVersionToApplyId || !routeVersionToApplyId.trim()) {
    throw new Error('routeVersionToApplyId is required');
  }

  const { fixedRouteApiUrl } = getApiUrls(environment);

  const payload = {
    type: 'applyrouteversiontoshifts',
    version: '1',
    aclConfig: { resource: 'shift-instances', operation: 'update' },
    aclContext: 'fixed_route',
    applyRouteVersionData: {
      routeVersionToApplyId,
    },
  };

  const response = await makeRequestWithRetry(
    fixedRouteApiUrl,
    'POST',
    createHeaders(jwtToken, productId, environment),
    undefined,
    payload,
    5,
    1000,
    environment,
  );

  const nested = response.data?.data || response.data;
  if (response.status !== 200 || nested?.success === false) {
    return {
      status: response.status,
      success: false,
      error: nested?.error || nested?.message || `HTTP ${response.status}`,
    };
  }
  return { status: response.status, success: true };
}

// ---------------------------------------------------------------------------
// Generic Ops shift-solution foundation (W7)
// ---------------------------------------------------------------------------

export interface OpsOperationSolutionContext {
  operationSolutionId: string;
  operationName: string;
  startDate: string;
  endDate: string;
  routeVersionIds: string[];
  shiftSolutionIds: string[];
}

export interface OpsVehicleAssignment {
  vehicleId: string;
  vehicleName?: string;
  fleetId?: string;
  fleetName?: string;
  sourceOperationSolutionId: string;
  sourceShiftSolutionId: string;
}

/**
 * Direction enum aligned with the real Hailer contract
 * (`hailer/src/.../create-shift-solution/request.ts`). Hailer accepts only
 * 'forward' | 'reverse' and throws on anything else (e.g. the old 'backward').
 */
export type ShiftSolutionDirection = 'forward' | 'reverse';

/**
 * Shift type aligned with the real Hailer contract: 'timetable' | 'shuttle'.
 */
export type ShiftSolutionType = 'timetable' | 'shuttle';

export interface RouteShiftIntent {
  operationSolutionId: string;
  routeVersionId: string;
  direction: ShiftSolutionDirection;
  startDate: string;
  endDate: string;
  numberOfRepeats: number;
  startStopIndex: number;
  shiftType: ShiftSolutionType;
  firstStopOffsetMins: number;
  vehicleAssignment?: OpsVehicleAssignment;
  driverId?: string;
}

export interface DatedShiftSolutionRequest {
  operationSolutionId: string;
  routeVersionId: string;
  direction: ShiftSolutionDirection;
  startDate: string;
  endDate: string;
  numberOfRepeats: number;
  startStopIndex: number;
  shiftType: ShiftSolutionType;
  firstStopOffsetMins: number;
  vehicleId?: string;
  driverId?: string;
}

export interface DatedShiftSolutionUpdateRequest extends DatedShiftSolutionRequest {
  shiftSolutionId: string;
}

export interface DatedShiftSolutionCommandSpec {
  type: string;
  aclContext: 'fixed_route' | 'operations';
  aclConfig: {
    resource: string;
    operation: 'create' | 'update' | 'view' | 'list';
  };
  endpointPath: '/context/fixed-route/c' | '/context/ops/c';
  dataKey: string;
}

export const ROUTE_UPLOAD_DATED_SHIFT_SOLUTION_COMMAND_SPEC: DatedShiftSolutionCommandSpec = {
  type: 'createshiftsolution',
  aclContext: 'fixed_route',
  aclConfig: {
    resource: 'shift-instances',
    operation: 'create',
  },
  endpointPath: '/context/fixed-route/c',
  dataKey: 'createShiftSolutionData',
};

export interface GenerateSolutionsCommandSpec {
  type: string;
  aclContext: 'fixed_route' | 'operations';
  aclConfig: {
    resource: string;
    operation: 'create' | 'update' | 'view' | 'list';
  };
  endpointPath: '/context/fixed-route/c' | '/context/ops/c';
  dataKey: string;
  confirmedByHailerSource: true;
}

export interface GenerateSolutionsRequest {
  operationSolutionId: string;
  date: string;
  timezone: string;
}

export interface DatedShiftSolutionCommandResponse {
  status: number;
  success: boolean;
  shiftSolutionId?: string;
  message?: string;
  error?: string;
  data?: unknown;
}

function getContextCommandUrl(environment: Environment, endpointPath: '/context/fixed-route/c' | '/context/ops/c'): string {
  return `${getApiBaseUrl(environment)}${endpointPath}`;
}

export function buildDatedShiftSolutionRequestFromIntent(intent: RouteShiftIntent): DatedShiftSolutionRequest {
  return {
    operationSolutionId: intent.operationSolutionId,
    routeVersionId: intent.routeVersionId,
    direction: intent.direction,
    startDate: intent.startDate,
    endDate: intent.endDate,
    numberOfRepeats: intent.numberOfRepeats,
    startStopIndex: intent.startStopIndex,
    shiftType: intent.shiftType,
    firstStopOffsetMins: intent.firstStopOffsetMins,
    vehicleId: intent.vehicleAssignment?.vehicleId,
    driverId: intent.driverId,
  };
}

function validateDatedShiftSolutionRequest(request: DatedShiftSolutionRequest): void {
  if (!request.operationSolutionId) throw new Error('operationSolutionId is required');
  if (!request.routeVersionId) throw new Error('routeVersionId is required');
  if (!request.startDate) throw new Error('startDate is required');
  if (!request.endDate) throw new Error('endDate is required');
  if (!Number.isInteger(request.numberOfRepeats) || request.numberOfRepeats < 0) {
    throw new Error('numberOfRepeats must be an integer >= 0');
  }
  if (!Number.isInteger(request.startStopIndex) || request.startStopIndex < 0) {
    throw new Error('startStopIndex must be an integer >= 0');
  }
  if (!Number.isFinite(request.firstStopOffsetMins)) {
    throw new Error('firstStopOffsetMins must be a number');
  }
  if (!request.shiftType || !request.shiftType.trim()) throw new Error('shiftType is required');
}

export function buildDatedShiftSolutionData(
  request: DatedShiftSolutionRequest | DatedShiftSolutionUpdateRequest,
): Record<string, unknown> {
  validateDatedShiftSolutionRequest(request);

  const data: Record<string, unknown> = {
    operationSolutionId: request.operationSolutionId,
    routeVersionId: request.routeVersionId,
    direction: request.direction,
    startDate: request.startDate,
    endDate: request.endDate,
    numberOfRepeats: request.numberOfRepeats,
    startStopIndex: request.startStopIndex,
    shiftType: request.shiftType,
    firstStopOffsetMins: request.firstStopOffsetMins,
  };

  if ('shiftSolutionId' in request) {
    data.shiftSolutionId = request.shiftSolutionId;
  }
  if (request.vehicleId) {
    data.vehicleId = request.vehicleId;
  }
  if (request.driverId) {
    data.driverId = request.driverId;
  } else if (!('shiftSolutionId' in request)) {
    // CREATE path only (no shiftSolutionId): send driverId explicitly as null
    // rather than omitting it. Ops/Hailer's availability check builds a Sequelize
    // WHERE of the form `{ [Op.or]: [{ driverId }, { vehicleId }] }`. An OMITTED
    // driverId arrives as `undefined`, which Sequelize rejects with
    // `WHERE parameter "driverId" has invalid "undefined" value`, aborting the
    // whole shift creation. Our shifts are vehicle-only (we never map a driver),
    // so this hit every create. `null` makes it a valid `driverId IS NULL`
    // predicate — vehicle-only shifts create cleanly with no Hailer change.
    //
    // For the UPDATE path (shiftSolutionId present) we keep OMITTING an absent
    // driverId so we never accidentally clear an existing shift's driver.
    data.driverId = null;
  }

  return data;
}

export function buildDatedShiftSolutionCommandPayload(
  commandSpec: DatedShiftSolutionCommandSpec,
  request: DatedShiftSolutionRequest | DatedShiftSolutionUpdateRequest,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    type: commandSpec.type,
    version: '1',
    aclConfig: commandSpec.aclConfig,
    aclContext: commandSpec.aclContext,
  };
  payload[commandSpec.dataKey] = buildDatedShiftSolutionData(request);
  return payload;
}

export function parseDatedShiftSolutionCommandResponse(
  response: { status: number; data: any },
): DatedShiftSolutionCommandResponse {
  const nested = response.data?.data || response.data;
  const success = response.status === 200 && nested?.success !== false;
  const shiftSolutionId =
    nested?.shiftSolutionId ||
    nested?.data?.shiftSolutionId ||
    nested?.createdShiftSolutionId ||
    nested?.data?.createdShiftSolutionId;

  if (success) {
    return {
      status: response.status,
      success: true,
      shiftSolutionId,
      message: nested?.message,
      data: nested,
    };
  }

  return {
    status: response.status,
    success: false,
    error: nested?.error || nested?.message || `HTTP ${response.status}`,
    data: nested,
  };
}

export function extractOperationSolutionContexts(
  response: UATOperationsSolutionsResponse,
): OpsOperationSolutionContext[] {
  const entries = response?.data?.entries || [];
  return entries.map((entry) => {
    const routeVersionIds = new Set<string>();
    const shiftSolutionIds = new Set<string>();
    for (const shift of entry.shiftSolutions || []) {
      if (shift.id) shiftSolutionIds.add(shift.id);
      if (shift.route?.id) routeVersionIds.add(shift.route.id);
    }
    return {
      operationSolutionId: entry.id,
      operationName: entry.name,
      startDate: entry.startDate,
      endDate: entry.endDate,
      routeVersionIds: Array.from(routeVersionIds),
      shiftSolutionIds: Array.from(shiftSolutionIds),
    };
  });
}

export function extractOpsVehicleAssignments(
  response: UATOperationsSolutionsResponse,
): OpsVehicleAssignment[] {
  const assignments = new Map<string, OpsVehicleAssignment>();
  for (const entry of response?.data?.entries || []) {
    for (const shift of entry.shiftSolutions || []) {
      const vehicleId = shift.vehicle?.id;
      if (!vehicleId) continue;
      if (!assignments.has(vehicleId)) {
        assignments.set(vehicleId, {
          vehicleId,
          vehicleName: shift.vehicle?.name,
          sourceOperationSolutionId: entry.id,
          sourceShiftSolutionId: shift.id,
        });
      }
    }
  }
  return Array.from(assignments.values());
}

export async function fetchOperationSolutionContexts(
  jwtToken: string,
  productId: string,
  date: string,
  timezone: string,
  environment: Environment = 'UAT',
): Promise<OpsOperationSolutionContext[]> {
  const response = await fetchOperationsSolutions(jwtToken, productId, date, timezone, environment);
  return extractOperationSolutionContexts(response);
}

export async function fetchOpsVehicleAssignments(
  jwtToken: string,
  productId: string,
  date: string,
  timezone: string,
  environment: Environment = 'UAT',
): Promise<OpsVehicleAssignment[]> {
  const response = await fetchOperationsSolutions(jwtToken, productId, date, timezone, environment);
  return extractOpsVehicleAssignments(response);
}

export async function createDatedShiftSolution(
  jwtToken: string,
  productId: string,
  commandSpec: DatedShiftSolutionCommandSpec,
  request: DatedShiftSolutionRequest,
  environment: Environment = 'UAT',
): Promise<DatedShiftSolutionCommandResponse> {
  if (!jwtToken || !jwtToken.trim()) throw new Error('JWT token is required');
  if (!productId || !productId.trim()) throw new Error('productId is required');
  const payload = buildDatedShiftSolutionCommandPayload(commandSpec, request);

  const response = await makeRequestWithRetry(
    getContextCommandUrl(environment, commandSpec.endpointPath),
    'POST',
    createHeaders(jwtToken, productId, environment),
    undefined,
    payload,
    5,
    1000,
    environment,
  );
  return parseDatedShiftSolutionCommandResponse(response);
}

export async function updateDatedShiftSolution(
  jwtToken: string,
  productId: string,
  commandSpec: DatedShiftSolutionCommandSpec,
  request: DatedShiftSolutionUpdateRequest,
  environment: Environment = 'UAT',
): Promise<DatedShiftSolutionCommandResponse> {
  if (!request.shiftSolutionId || !request.shiftSolutionId.trim()) {
    throw new Error('shiftSolutionId is required for update');
  }

  const payload = buildDatedShiftSolutionCommandPayload(commandSpec, request);
  const response = await makeRequestWithRetry(
    getContextCommandUrl(environment, commandSpec.endpointPath),
    'POST',
    createHeaders(jwtToken, productId, environment),
    undefined,
    payload,
    5,
    1000,
    environment,
  );
  return parseDatedShiftSolutionCommandResponse(response);
}

export async function generateOperationOrShiftSolutions(
  jwtToken: string,
  productId: string,
  commandSpec: GenerateSolutionsCommandSpec,
  request: GenerateSolutionsRequest,
  environment: Environment = 'UAT',
): Promise<DatedShiftSolutionCommandResponse> {
  if (!commandSpec.confirmedByHailerSource) {
    throw new Error('Generation command blocked: command spec must be confirmed by Hailer source');
  }
  const payload: Record<string, unknown> = {
    type: commandSpec.type,
    version: '1',
    aclConfig: commandSpec.aclConfig,
    aclContext: commandSpec.aclContext,
  };
  payload[commandSpec.dataKey] = request;

  const response = await makeRequestWithRetry(
    getContextCommandUrl(environment, commandSpec.endpointPath),
    'POST',
    createHeaders(jwtToken, productId, environment),
    undefined,
    payload,
    5,
    1000,
    environment,
  );
  return parseDatedShiftSolutionCommandResponse(response);
}

/** Route-upload adapter payload (W9 orchestration). */
export interface CreateDatedShiftSolutionPayload {
  operationSolutionId: string;
  routeVersionId: string;
  startTime: string;
  endTime: string;
  timezone: string;
  vehicleId?: string;
}

export interface CreateDatedShiftSolutionResponse {
  status: number;
  success: boolean;
  createdShiftSolutionId?: string;
  integrationPending?: boolean;
  error?: string;
}

/**
 * Route-upload entry point for dated shift-solution creation.
 * Uses W7 command wrapper when a confirmed command spec is supplied; otherwise
 * returns integration-pending so uploads can warn-and-continue.
 */
export async function createDatedShiftSolutionForRouteUpload(
  jwtToken: string,
  productId: string,
  payload: CreateDatedShiftSolutionPayload,
  environment: Environment = 'UAT',
  commandSpec: DatedShiftSolutionCommandSpec | null = ROUTE_UPLOAD_DATED_SHIFT_SOLUTION_COMMAND_SPEC,
): Promise<CreateDatedShiftSolutionResponse> {
  if (!commandSpec) {
    console.warn('[createDatedShiftSolutionForRouteUpload] No command spec; payload', payload);
    return {
      status: 501,
      success: false,
      integrationPending: true,
      error:
        'Dated shift-solution API command spec is not configured. Route upload completed; assign shifts manually in Ops or configure the Hailer command spec.',
    };
  }

  const request: DatedShiftSolutionRequest = {
    operationSolutionId: payload.operationSolutionId,
    routeVersionId: payload.routeVersionId,
    direction: 'forward',
    startDate: payload.startTime,
    endDate: payload.endTime,
    numberOfRepeats: 0,
    startStopIndex: 0,
    shiftType: 'timetable',
    firstStopOffsetMins: 0,
    vehicleId: payload.vehicleId,
  };

  const result = await createDatedShiftSolution(
    jwtToken,
    productId,
    commandSpec,
    request,
    environment,
  );

  return {
    status: result.status,
    success: result.success,
    createdShiftSolutionId: result.shiftSolutionId,
    error: result.error,
  };
}
